import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { Row, Col, Card, Form, Button, ButtonGroup, Table, Modal, Badge, Tabs, Tab, Spinner, InputGroup } from 'react-bootstrap';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import CategoryIconPicker from '../../components/common/CategoryIconPicker';
import { getCategoryIconMeta } from '../../constants/categoryIcons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BANNER_COLOR_PRESETS = [
    { id: 'blue', name: 'Azul Rutex', start: '#1E3A8A', end: '#3B82F6', text: '#FFFFFF', btnBg: '#183EEB', btnText: '#FFFFFF' },
    { id: 'orange', name: 'Naranja Ofertas', start: '#C2410C', end: '#EA580C', text: '#FFFFFF', btnBg: '#FFFFFF', btnText: '#C2410C' },
    { id: 'red', name: 'Rojo Descuento', start: '#991B1B', end: '#DC2626', text: '#FFFFFF', btnBg: '#FFFFFF', btnText: '#991B1B' },
    { id: 'green', name: 'Verde Ahorro', start: '#065F46', end: '#10B981', text: '#FFFFFF', btnBg: '#FFFFFF', btnText: '#065F46' },
    { id: 'purple', name: 'Púrpura Novedades', start: '#4C1D95', end: '#7C3AED', text: '#FFFFFF', btnBg: '#FFFFFF', btnText: '#4C1D95' },
    { id: 'dark', name: 'Oscuro Premium', start: '#0F172A', end: '#334155', text: '#FFFFFF', btnBg: '#183EEB', btnText: '#FFFFFF' },
    { id: 'light', name: 'Claro Neutro', start: '#E2E8F0', end: '#F1F5F9', text: '#0F172A', btnBg: '#183EEB', btnText: '#FFFFFF' }
];

const MasterDataConfig = () => {
    const { t } = useTranslation();
    const { isDark } = useContext(ThemeContext) || {};
    const [activeTab, setActiveTab] = useState('categories');

    // 1. Categorías state
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'root' | 'sub'

    const rootCategories = useMemo(() => {
        return categories.filter(c => !c.parent_id);
    }, [categories]);

    const subCategories = useMemo(() => {
        return categories.filter(c => !!c.parent_id);
    }, [categories]);

    const filteredCategories = useMemo(() => {
        if (categoryFilter === 'root') return rootCategories;
        if (categoryFilter === 'sub') return subCategories;
        return categories;
    }, [categories, categoryFilter, rootCategories, subCategories]);

    // 2. Familias (Marcas) state
    const [families, setFamilies] = useState([]);
    const [loadingFamilies, setLoadingFamilies] = useState(false);
    const [uploadingBrandImage, setUploadingBrandImage] = useState(false);
    const brandFileInputRef = useRef(null);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(false);

    // 4. Motivos de No Venta state
    const [noSaleReasons, setNoSaleReasons] = useState([]);
    const [loadingReasons, setLoadingReasons] = useState(false);

    // 4.1. Motivos de Rechazo de Pagos state
    const [paymentRejectionReasons, setPaymentRejectionReasons] = useState([]);
    const [loadingPaymentRejectionReasons, setLoadingPaymentRejectionReasons] = useState(false);

    // 4.2. Banners de Inicio state
    const [banners, setBanners] = useState([]);
    const [loadingBanners, setLoadingBanners] = useState(false);
    // 4.2. Labores de Apoyo (Mercaderista) state
    const [merchandisingTasks, setMerchandisingTasks] = useState([]);
    const [loadingMerchandisingTasks, setLoadingMerchandisingTasks] = useState(false);

    // 5. Configuración Global state
    const [settingsForm, setSettingsForm] = useState({
        timezone: 'America/Caracas',
        currency: 'USD',
        iva_porcentaje: 0.00,
        credit_settings: {
            default_dias_credito: 8,
            max_facturas_vencidas_bloqueo: 2,
            canales: {
                transformadores: { dias_credito: 8, limite_credito: 500 },
                supermercados_bodegones: { dias_credito: 8, limite_credito: 1200 },
                tiendas_bodegas: { dias_credito: 8, limite_credito: 300 }
            }
        },
        propagate_to_existing_customers: false
    });
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    // --- MODALES STATE ---
    // Modal Categoría
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryForm, setCategoryForm] = useState({ id: null, prefijo_sku: '', nombre: '', icono: '', icono_color: '#183EEB', parent_id: '', is_active: true });
    const [savingCategory, setSavingCategory] = useState(false);

    // Modal Familia (Marca)
    const [showFamilyModal, setShowFamilyModal] = useState(false);
    const [familyForm, setFamilyForm] = useState({ id: null, code: '', name: '', imagen_url: '', is_active: true });
    const [savingFamily, setSavingFamily] = useState(false);

    // Modal Método de Pago
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        id: null,
        code: '',
        type: 'pago_movil',
        title: '',
        subtitle: '',
        currency: 'VES',
        bank_name: '',
        bank_code: '',
        document: '',
        phone: '',
        account_number: '',
        email: '',
        holder_name: '',
        instructions: '',
        is_active: true
    });
    const [savingPayment, setSavingPayment] = useState(false);

    // Modal Motivo de No Venta
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [reasonForm, setReasonForm] = useState({ id: null, code: '', name: '', is_active: true });
    const [savingReason, setSavingReason] = useState(false);

    // Modal Motivo de Rechazo de Pago
    const [showPaymentRejectionModal, setShowPaymentRejectionModal] = useState(false);
    const [paymentRejectionForm, setPaymentRejectionForm] = useState({ id: null, code: '', name: '', is_active: true });
    const [savingPaymentRejection, setSavingPaymentRejection] = useState(false);

    // Modal Banner de Inicio
    const [showBannerModal, setShowBannerModal] = useState(false);
    const [catalogProducts, setCatalogProducts] = useState([]);
    const [loadingCatalogProducts, setLoadingCatalogProducts] = useState(false);
    const [bannerForm, setBannerForm] = useState({
        id: null,
        tag: '',
        titulo: '',
        subtitulo: '',
        action_label: 'Ver ahora',
        action_type: 'catalog',
        target_id: '',
        bg_color_start: '#1E3A8A',
        bg_color_end: '#3B82F6',
        text_color: '#FFFFFF',
        btn_bg_color: '#183EEB',
        btn_text_color: '#FFFFFF',
        orden: 1,
        is_active: true
    });
    const [savingBanner, setSavingBanner] = useState(false);

    // Asistente Visual de Deeplinks / Filtros
    const [deeplinkBuilder, setDeeplinkBuilder] = useState({
        mode: 'catalog', // 'catalog' | 'screen'
        screen: 'InvoicesPage',
        categoryId: '',
        brandId: '',
        searchQuery: ''
    });

    const generateDeeplinkUrl = useCallback((builder) => {
        if (builder.mode === 'screen') {
            return builder.screen || 'InvoicesPage';
        }
        const params = [];
        if (builder.categoryId) params.push(`CategoryId=${encodeURIComponent(builder.categoryId)}`);
        if (builder.brandId) params.push(`BrandId=${encodeURIComponent(builder.brandId)}`);
        if (builder.searchQuery?.trim()) params.push(`SearchQuery=${encodeURIComponent(builder.searchQuery.trim())}`);

        return params.length > 0 ? `CatalogPage?${params.join('&')}` : 'CatalogPage';
    }, []);

    const updateDeeplinkBuilder = (updater) => {
        setDeeplinkBuilder(prev => {
            const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
            const newUrl = generateDeeplinkUrl(next);
            setBannerForm(bf => ({ ...bf, target_id: newUrl }));
            return next;
        });
    };

    const [productCategoryFilter, setProductCategoryFilter] = useState('');

    // Filtros inteligentes para blindar el Asistente de Deeplinks y Banners
    const availableBrandsForSelectedCategory = useMemo(() => {
        if (!deeplinkBuilder.categoryId) return families;
        // Obtener marcas únicas que realmente tienen productos en la categoría seleccionada
        const brandsInCat = new Set(
            catalogProducts
                .filter(p => p.categoria_id === deeplinkBuilder.categoryId || p.subcategory_id === deeplinkBuilder.categoryId)
                .map(p => (p.marca || '').toUpperCase().trim())
                .filter(Boolean)
        );
        return families.filter(f => brandsInCat.has((f.name || '').toUpperCase().trim()));
    }, [deeplinkBuilder.categoryId, families, catalogProducts]);

    const matchingProductsStats = useMemo(() => {
        if (deeplinkBuilder.mode !== 'catalog') return null;
        let total = 0;
        let withStock = 0;
        let outOfStock = 0;
        const catId = deeplinkBuilder.categoryId;
        const brandCode = (deeplinkBuilder.brandId || '').toUpperCase().trim();
        const search = (deeplinkBuilder.searchQuery || '').toLowerCase().trim();

        for (const p of catalogProducts) {
            if (catId && p.categoria_id !== catId && p.subcategory_id !== catId) continue;
            if (brandCode && (p.marca || '').toUpperCase().trim() !== brandCode) continue;
            if (search) {
                const inName = (p.nombre || '').toLowerCase().includes(search);
                const inSku = (p.sku || '').toLowerCase().includes(search);
                const inBrand = (p.marca || '').toLowerCase().includes(search);
                if (!inName && !inSku && !inBrand) continue;
            }
            total++;
            const hasStock = (Number(p.stock_real) || 0) > 0 && (p.is_active !== false);
            if (hasStock) {
                withStock++;
            } else {
                outOfStock++;
            }
        }
        return { total, withStock, outOfStock };
    }, [deeplinkBuilder, catalogProducts]);

    const filteredProductsForAction = useMemo(() => {
        if (!productCategoryFilter) return catalogProducts;
        return catalogProducts.filter(p => p.categoria_id === productCategoryFilter || p.subcategory_id === productCategoryFilter);
    }, [catalogProducts, productCategoryFilter]);
    // Modal Labor de Apoyo (Mercaderista)
    const [showMerchandisingTaskModal, setShowMerchandisingTaskModal] = useState(false);
    const [merchandisingTaskForm, setMerchandisingTaskForm] = useState({ id: null, code: '', name: '', is_active: true });
    const [savingMerchandisingTask, setSavingMerchandisingTask] = useState(false);

    // --- FETCH FUNCTIONS ---

    const fetchCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const res = await axiosInstance.get('/core/masters/categories');
            setCategories(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_categories', 'Error al cargar las categorías de productos'));
        } finally {
            setLoadingCategories(false);
        }
    }, [t]);

    const fetchFamilies = useCallback(async () => {
        setLoadingFamilies(true);
        try {
            const res = await axiosInstance.get('/core/masters/brands');
            setFamilies(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_families', 'Error al cargar las familias de productos'));
        } finally {
            setLoadingFamilies(false);
        }
    }, [t]);

    const fetchPaymentMethods = useCallback(async () => {
        setLoadingPayments(true);
        try {
            const res = await axiosInstance.get('/core/masters/payment_methods');
            const data = res.data.data || [];
            const formatted = data.map(item => {
                let details = item.details;
                if (!details && typeof item.value === 'string' && item.value.trim().startsWith('{')) {
                    try {
                        details = JSON.parse(item.value);
                    } catch (e) {
                        details = null;
                    }
                }
                const nameLower = (item.name || '').toLowerCase();
                const inferType = nameLower.includes('zelle') 
                    ? 'zelle' 
                    : (nameLower.includes('efectivo') 
                        ? 'efectivo' 
                        : (nameLower.includes('transf') ? 'transferencia' : 'pago_movil'));

                return {
                    ...item,
                    details: details || {
                        title: typeof item.value === 'string' && !item.value.trim().startsWith('{') ? item.value : item.name,
                        subtitle: '',
                        type: inferType,
                        currency: inferType === 'zelle' || inferType === 'efectivo' ? 'USD' : 'VES'
                    }
                };
            });
            setPaymentMethods(formatted);
        } catch (err) {
            toast.error(t('toast_error_load_payment_methods', 'Error al cargar los métodos de pago'));
        } finally {
            setLoadingPayments(false);
        }
    }, [t]);

    const fetchNoSaleReasons = useCallback(async () => {
        setLoadingReasons(true);
        try {
            const res = await axiosInstance.get('/core/masters/no_sale_reasons');
            setNoSaleReasons(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_no_sale_reasons', 'Error al cargar los motivos de no venta'));
        } finally {
            setLoadingReasons(false);
        }
    }, [t]);

    const fetchPaymentRejectionReasons = useCallback(async () => {
        setLoadingPaymentRejectionReasons(true);
        try {
            const res = await axiosInstance.get('/core/masters/payment_rejection_reasons');
            setPaymentRejectionReasons(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_payment_rejection_reasons', 'Error al cargar los motivos de rechazo de pago'));
        } finally {
            setLoadingPaymentRejectionReasons(false);
        }
    }, [t]);

    const fetchBanners = useCallback(async () => {
        setLoadingBanners(true);
        try {
            const res = await axiosInstance.get('/admin/banners');
            setBanners(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_banners', 'Error al cargar los banners de inicio'));
        } finally {
            setLoadingBanners(false);
        }
    }, [t]);

    const fetchCatalogProducts = useCallback(async () => {
        setLoadingCatalogProducts(true);
        try {
            const res = await axiosInstance.get('/admin/catalog?per_page=500');
            setCatalogProducts(res.data.data || []);
        } catch (err) {
            console.error('Error al cargar productos para banners:', err);
        } finally {
            setLoadingCatalogProducts(false);
        }
    }, []);

    const fetchMerchandisingTasks = useCallback(async () => {
        setLoadingMerchandisingTasks(true);
        try {
            const res = await axiosInstance.get('/core/masters/merchandising_tasks');
            setMerchandisingTasks(res.data.data || []);
        } catch (err) {
            toast.error(t('toast_error_load_merchandising_tasks', 'Error al cargar las labores de apoyo'));
        } finally {
            setLoadingMerchandisingTasks(false);
        }
    }, [t]);

    const fetchSettings = useCallback(async () => {
        setLoadingSettings(true);
        try {
            const res = await axiosInstance.get('/core/settings');
            const data = res.data.data;
            if (data) {
                setSettingsForm(prev => ({
                    ...prev,
                    timezone: data.timezone || 'America/Caracas',
                    currency: data.currency || 'USD',
                    iva_porcentaje: data.iva_porcentaje !== undefined ? data.iva_porcentaje : 0.00,
                    credit_settings: data.credit_settings || prev.credit_settings
                }));
            }
        } catch (err) {
            toast.error(t('toast_error_load_settings', 'Error al cargar la configuración global'));
        } finally {
            setLoadingSettings(false);
        }
    }, [t]);

      useEffect(() => {
        if (activeTab === 'categories') fetchCategories();
        if (activeTab === 'families') fetchFamilies();
        if (activeTab === 'payments') fetchPaymentMethods();
        if (activeTab === 'reasons') fetchNoSaleReasons();
        if (activeTab === 'payment_rejection_reasons') fetchPaymentRejectionReasons();
        if (activeTab === 'merchandising_tasks') fetchMerchandisingTasks();
        if (activeTab === 'banners') {
            fetchBanners();
            if (categories.length === 0) fetchCategories();
            if (families.length === 0) fetchFamilies();
        }
        if (activeTab === 'settings') fetchSettings();
    }, [
        activeTab,
        fetchCategories,
        fetchFamilies,
        fetchPaymentMethods,
        fetchNoSaleReasons,
        fetchPaymentRejectionReasons,
        fetchMerchandisingTasks,
        fetchBanners,
        fetchSettings,
        categories.length,
        families.length
    ]);

    // --- TOGGLE HANDLER ---

    const handleToggleStatus = async (type, item) => {
        const newStatus = !item.is_active;
        try {
            const res = await axiosInstance.patch(`/core/masters/${type}/${item.id}/toggle`, {
                is_active: newStatus
            });
            toast.success(res.data.message || t('toast_success_status_updated', 'Estado actualizado correctamente'));
            
            if (type === 'categories') {
                setCategories(prev => prev.map(c => c.id === item.id ? { ...c, is_active: newStatus } : c));
            } else if (type === 'brands') {
                setFamilies(prev => prev.map(f => f.id === item.id ? { ...f, is_active: newStatus } : f));
            } else if (type === 'payment_methods') {
                setPaymentMethods(prev => prev.map(p => p.id === item.id ? { ...p, is_active: newStatus } : p));
            } else if (type === 'no_sale_reasons') {
                setNoSaleReasons(prev => prev.map(r => r.id === item.id ? { ...r, is_active: newStatus } : r));
            } else if (type === 'payment_rejection_reasons') {
                setPaymentRejectionReasons(prev => prev.map(r => r.id === item.id ? { ...r, is_active: newStatus } : r));
            } else if (type === 'merchandising_tasks') {
                setMerchandisingTasks(prev => prev.map(t => t.id === item.id ? { ...t, is_active: newStatus } : t));
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al cambiar el estado del registro'));
        }
    };

    // --- DELETE HANDLER ---
    const handleDeleteRecord = async (type, item) => {
        const confirmMsg = t('confirm_delete_master_record', '¿Está seguro de que desea eliminar este registro? Esta acción no se puede deshacer.');
        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await axiosInstance.delete(`/core/masters/${type}/${item.id}`);
            toast.success(res.data?.message || t('toast_success_record_deleted', 'Registro eliminado correctamente'));
            if (type === 'categories') fetchCategories();
            if (type === 'brands') fetchFamilies();
            if (type === 'payment_methods') fetchPaymentMethods();
            if (type === 'no_sale_reasons') fetchNoSaleReasons();
            if (type === 'payment_rejection_reasons') fetchPaymentRejectionReasons();
            if (type === 'merchandising_tasks') fetchMerchandisingTasks();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al eliminar el registro'));
        }
    };

    // --- SAVE CATEGORY ---
    const handleSaveCategory = async (e) => {
        e.preventDefault();
        if (!categoryForm.nombre.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingCategory(true);
        try {
            const payload = {
                name: categoryForm.prefijo_sku,
                value: categoryForm.nombre,
                icono: categoryForm.icono || null,
                icono_color: categoryForm.icono_color || '#183EEB',
                parent_id: categoryForm.parent_id || null,
            };
            if (categoryForm.id) {
                await axiosInstance.put(`/core/masters/categories/${categoryForm.id}`, payload);
            } else {
                await axiosInstance.post('/core/masters/categories', payload);
            }
            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowCategoryModal(false);
            fetchCategories();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar la categoría'));
        } finally {
            setSavingCategory(false);
        }
    };

    // --- SAVE FAMILY (BRAND) ---
    const handleSaveFamily = async (e) => {
        e.preventDefault();
        if (!familyForm.name.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingFamily(true);
        try {
            const payload = {
                name: familyForm.code,
                value: familyForm.name,
                imagen_url: familyForm.imagen_url || null
            };
            if (familyForm.id) {
                await axiosInstance.put(`/core/masters/brands/${familyForm.id}`, payload);
            } else {
                await axiosInstance.post('/core/masters/brands', payload);
            }
            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowFamilyModal(false);
            fetchFamilies();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar la familia'));
        } finally {
            setSavingFamily(false);
        }
    };

    // --- SAVE PAYMENT METHOD ---
    const handleSavePaymentMethod = async (e) => {
        e.preventDefault();
        if (!paymentForm.title.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingPayment(true);
        try {
            const structuredData = {
                title: paymentForm.title.trim(),
                subtitle: paymentForm.subtitle.trim(),
                type: paymentForm.type,
                currency: paymentForm.currency,
                bank_name: paymentForm.bank_name.trim(),
                bank_code: paymentForm.bank_code.trim(),
                document: paymentForm.document.trim(),
                phone: paymentForm.phone.trim(),
                account_number: paymentForm.account_number.trim(),
                email: paymentForm.email.trim(),
                holder_name: paymentForm.holder_name.trim(),
                instructions: paymentForm.instructions.trim()
            };

            const internalCode = paymentForm.code ? paymentForm.code.toUpperCase().replace(/\s+/g, '_') : paymentForm.type.toUpperCase();

            if (paymentForm.id) {
                await axiosInstance.put(`/core/masters/payment_methods/${paymentForm.id}`, {
                    name: internalCode,
                    value: structuredData
                });
            } else {
                await axiosInstance.post('/core/masters/payment_methods', {
                    name: internalCode,
                    value: structuredData
                });
            }

            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowPaymentModal(false);
            fetchPaymentMethods();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar el método de pago'));
        } finally {
            setSavingPayment(false);
        }
    };

    // --- SAVE REASON ---
    const handleSaveReason = async (e) => {
        e.preventDefault();
        if (!reasonForm.name.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingReason(true);
        try {
            if (reasonForm.id) {
                await axiosInstance.put(`/core/masters/no_sale_reasons/${reasonForm.id}`, {
                    name: reasonForm.code,
                    value: reasonForm.name
                });
            } else {
                await axiosInstance.post('/core/masters/no_sale_reasons', {
                    name: reasonForm.code,
                    value: reasonForm.name
                });
            }
            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowReasonModal(false);
            fetchNoSaleReasons();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar el motivo'));
        } finally {
            setSavingReason(false);
        }
    };

    // --- SAVE PAYMENT REJECTION REASON ---
    const handleSavePaymentRejection = async (e) => {
        e.preventDefault();
        if (!paymentRejectionForm.name.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingPaymentRejection(true);
        try {
            if (paymentRejectionForm.id) {
                await axiosInstance.put(`/core/masters/payment_rejection_reasons/${paymentRejectionForm.id}`, {
                    name: paymentRejectionForm.code,
                    value: paymentRejectionForm.name
                });
            } else {
                await axiosInstance.post('/core/masters/payment_rejection_reasons', {
                    name: paymentRejectionForm.code,
                    value: paymentRejectionForm.name
                });
            }
            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowPaymentRejectionModal(false);
            fetchPaymentRejectionReasons();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar el motivo de rechazo'));
        } finally {
            setSavingPaymentRejection(false);
        }
    };

    // --- SAVE MERCHANDISING TASK ---
    const handleSaveMerchandisingTask = async (e) => {
        e.preventDefault();
        if (!merchandisingTaskForm.name.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete todos los campos obligatorios.'));
            return;
        }
        setSavingMerchandisingTask(true);
        try {
            if (merchandisingTaskForm.id) {
                await axiosInstance.put(`/core/masters/merchandising_tasks/${merchandisingTaskForm.id}`, {
                    name: merchandisingTaskForm.code,
                    value: merchandisingTaskForm.name
                });
            } else {
                await axiosInstance.post('/core/masters/merchandising_tasks', {
                    name: merchandisingTaskForm.code,
                    value: merchandisingTaskForm.name
                });
            }
            toast.success(t('toast_success_record_saved', 'Registro guardado con éxito'));
            setShowMerchandisingTaskModal(false);
            fetchMerchandisingTasks();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error_save_task', 'Error al guardar la labor de apoyo'));
        } finally {
            setSavingMerchandisingTask(false);
        }
    };

    // --- SAVE SETTINGS ---
    const handleSaveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            const res = await axiosInstance.put('/core/settings', {
                timezone: settingsForm.timezone,
                currency: settingsForm.currency,
                iva_porcentaje: parseFloat(settingsForm.iva_porcentaje) || 0.00,
                ui: {
                    primary_color: '#183EEB',
                    secondary_color: '#00024F',
                    logo_url: '/images/logo.png'
                },
                credit_settings: settingsForm.credit_settings,
                propagate_to_existing_customers: settingsForm.propagate_to_existing_customers
            });
            const count = res.data?.propagated_customers_count || 0;
            if (settingsForm.propagate_to_existing_customers && count > 0) {
                toast.success(t('toast_success_settings_with_propagation', 'Configuración global actualizada y aplicada a {{count}} clientes existentes.', { count }));
            } else {
                toast.success(t('toast_success_settings_saved', 'Configuración global actualizada correctamente'));
            }
            setSettingsForm(prev => ({ ...prev, propagate_to_existing_customers: false }));
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('error', 'Error al guardar la configuración global');
            toast.error(errorMsg === '060' ? t('superadmin_only_hint', 'Solo el rol SuperAdmin puede modificar la configuración global') : errorMsg);
        } finally {
            setSavingSettings(false);
        }
    };

    // --- MODAL OPENERS ---

    const openCategoryModal = (cat = null, defaultParentId = null) => {
        setCategoryForm(cat ? {
            id: cat.id,
            prefijo_sku: cat.name || '',
            nombre: cat.value || '',
            icono: cat.icono || '',
            icono_color: cat.icono_color || cat.icon_color || '#183EEB',
            parent_id: cat.parent_id || '',
            is_active: cat.is_active !== undefined ? cat.is_active : true
        } : {
            id: null,
            prefijo_sku: '',
            nombre: '',
            icono: '',
            icono_color: '#183EEB',
            parent_id: defaultParentId || '',
            is_active: true
        });
        setShowCategoryModal(true);
    };

    const openFamilyModal = (fam = null) => {
        setFamilyForm(fam ? {
            id: fam.id,
            code: fam.name || '',
            name: fam.value || '',
            imagen_url: fam.imagen_url || '',
            is_active: fam.is_active
        } : {
            id: null,
            code: '',
            name: '',
            imagen_url: '',
            is_active: true
        });
        setShowFamilyModal(true);
    };

    const handleBrandImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error(t('toast_error_image_size', 'La imagen no debe superar los 10MB'));
            return;
        }

        setUploadingBrandImage(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await axiosInstance.post('/core/masters/brands/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data?.url) {
                setFamilyForm(prev => ({ ...prev, imagen_url: res.data.url }));
                toast.success(t('toast_image_uploaded', 'Logo subido con éxito al bucket'));
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t('toast_error_upload_image', 'Error al subir la imagen al bucket'));
        } finally {
            setUploadingBrandImage(false);
            if (e.target) e.target.value = '';
        }
    };

    const openPaymentModal = (item = null) => {
        if (item) {
            const d = item.details || {};
            setPaymentForm({
                id: item.id,
                code: item.name || '',
                type: d.type || 'pago_movil',
                title: d.title || item.name || '',
                subtitle: d.subtitle || '',
                currency: d.currency || 'VES',
                bank_name: d.bank_name || '',
                bank_code: d.bank_code || '',
                document: d.document || '',
                phone: d.phone || '',
                account_number: d.account_number || '',
                email: d.email || '',
                holder_name: d.holder_name || '',
                instructions: d.instructions || '',
                is_active: item.is_active
            });
        } else {
            setPaymentForm({
                id: null,
                code: '',
                type: 'pago_movil',
                title: '',
                subtitle: '',
                currency: 'VES',
                bank_name: '',
                bank_code: '',
                document: '',
                phone: '',
                account_number: '',
                email: '',
                holder_name: '',
                instructions: '',
                is_active: true
            });
        }
        setShowPaymentModal(true);
    };

    const openReasonModal = (reason = null) => {
        setReasonForm(reason ? {
            id: reason.id,
            code: reason.name || '',
            name: reason.value || '',
            is_active: reason.is_active
        } : {
            id: null,
            code: '',
            name: '',
            is_active: true
        });
        setShowReasonModal(true);
    };

    const openPaymentRejectionModal = (reason = null) => {
        setPaymentRejectionForm(reason ? {
            id: reason.id,
            code: reason.name || '',
            name: reason.value || '',
            is_active: reason.is_active
        } : {
            id: null,
            code: '',
            name: '',
            is_active: true
        });
        setShowPaymentRejectionModal(true);
    };

    // --- BANNERS HANDLERS ---
    const openBannerModal = (banner = null) => {
        if (categories.length === 0) fetchCategories();
        if (families.length === 0) fetchFamilies();
        if (catalogProducts.length === 0) fetchCatalogProducts();

        if (banner) {
            const rawTarget = banner.target_id || '';
            if (banner.action_type === 'custom_deeplink') {
                if (['InvoicesPage', 'CartPage', 'FaqPage', 'NotificationsPage', 'ProfilePage'].includes(rawTarget)) {
                    setDeeplinkBuilder({
                        mode: 'screen',
                        screen: rawTarget,
                        categoryId: '',
                        brandId: '',
                        searchQuery: ''
                    });
                } else {
                    const query = rawTarget.includes('?') ? rawTarget.split('?')[1] : '';
                    const searchParams = new URLSearchParams(query);
                    setDeeplinkBuilder({
                        mode: 'catalog',
                        screen: 'InvoicesPage',
                        categoryId: searchParams.get('CategoryId') || '',
                        brandId: searchParams.get('BrandId') || '',
                        searchQuery: searchParams.get('SearchQuery') || ''
                    });
                }
            } else {
                setDeeplinkBuilder({
                    mode: 'catalog',
                    screen: 'InvoicesPage',
                    categoryId: '',
                    brandId: '',
                    searchQuery: ''
                });
            }

            setBannerForm({
                id: banner.id,
                tag: banner.tag || '',
                titulo: banner.titulo || '',
                subtitulo: banner.subtitulo || '',
                action_label: banner.action_label || 'Ver ahora',
                action_type: banner.action_type || 'catalog',
                target_id: banner.target_id || '',
                bg_color_start: banner.bg_color_start || '#1E3A8A',
                bg_color_end: banner.bg_color_end || '#3B82F6',
                text_color: banner.text_color || '#FFFFFF',
                btn_bg_color: banner.btn_bg_color || '#183EEB',
                btn_text_color: banner.btn_text_color || '#FFFFFF',
                orden: banner.orden ?? 1,
                is_active: banner.is_active ?? true
            });
        } else {
            setDeeplinkBuilder({
                mode: 'catalog',
                screen: 'InvoicesPage',
                categoryId: '',
                brandId: '',
                searchQuery: ''
            });

            const nextOrden = banners.length > 0 ? Math.max(...banners.map(b => b.orden || 0)) + 1 : 1;
            setBannerForm({
                id: null,
                tag: '',
                titulo: '',
                subtitulo: '',
                action_label: 'Ver ahora',
                action_type: 'catalog',
                target_id: '',
                bg_color_start: '#1E3A8A',
                bg_color_end: '#3B82F6',
                text_color: '#FFFFFF',
                btn_bg_color: '#183EEB',
                btn_text_color: '#FFFFFF',
                orden: nextOrden,
                is_active: true
            });
        }
        setProductCategoryFilter('');
        setShowBannerModal(true);
    };

    const handleSaveBanner = async (e) => {
        e.preventDefault();
        if (!bannerForm.titulo.trim()) {
            toast.warning(t('toast_warning_fill_fields', 'Por favor complete el título del banner.'));
            return;
        }
        if (bannerForm.action_type === 'category' && !bannerForm.target_id) {
            toast.warning('Por favor seleccione la categoría destino para el banner.');
            return;
        }
        if (bannerForm.action_type === 'brand' && !bannerForm.target_id) {
            toast.warning('Por favor seleccione la marca destino para el banner.');
            return;
        }
        if (bannerForm.action_type === 'product' && !bannerForm.target_id) {
            toast.warning('Por favor seleccione el producto destino para el banner.');
            return;
        }
        if (bannerForm.action_type === 'url' && !bannerForm.target_id?.trim()) {
            toast.warning('Por favor ingrese el enlace URL o número de WhatsApp.');
            return;
        }
        if (bannerForm.action_type === 'custom_deeplink' && !bannerForm.target_id?.trim()) {
            toast.warning('Por favor ingrese la ruta personalizada del deeplink.');
            return;
        }
        setSavingBanner(true);
        try {
            if (bannerForm.id) {
                await axiosInstance.put(`/admin/banners/${bannerForm.id}`, bannerForm);
            } else {
                await axiosInstance.post('/admin/banners', bannerForm);
            }
            toast.success(t('toast_success_record_saved', 'Banner guardado con éxito'));
            setShowBannerModal(false);
            fetchBanners();
        } catch (err) {
            toast.error(err.response?.data?.message || t('error', 'Error al guardar el banner'));
        } finally {
            setSavingBanner(false);
        }
    };

    const handleDeleteBanner = async (banner) => {
        if (!window.confirm(`¿Está seguro de eliminar el banner "${banner.titulo}"?`)) return;
        try {
            await axiosInstance.delete(`/admin/banners/${banner.id}`);
            toast.success(t('toast_success_record_deleted', 'Banner eliminado con éxito'));
            fetchBanners();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al eliminar el banner');
        }
    };

    const handleToggleBannerStatus = async (banner) => {
        try {
            await axiosInstance.patch(`/admin/banners/${banner.id}/toggle-status`);
            toast.success(t('toast_success_status_updated', 'Estado actualizado correctamente'));
            setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, is_active: !b.is_active } : b));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al actualizar el estado del banner');
        }
    };

    const handleMoveBanner = async (index, direction) => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= banners.length) return;

        const updated = [...banners];
        const temp = updated[index];
        updated[index] = updated[targetIndex];
        updated[targetIndex] = temp;

        const orders = updated.map((b, idx) => ({
            id: b.id,
            orden: idx + 1
        }));

        setBanners(updated.map((b, idx) => ({ ...b, orden: idx + 1 })));

        try {
            await axiosInstance.post('/admin/banners/reorder', { orders });
            toast.success('Orden de banners actualizado');
        } catch (err) {
            toast.error('Error al guardar el nuevo orden de banners');
            fetchBanners();
          }
        };
    const openMerchandisingTaskModal = (task = null) => {
        setMerchandisingTaskForm(task ? {
            id: task.id,
            code: task.name || '',
            name: task.value || '',
            is_active: task.is_active
        } : {
            id: null,
            code: '',
            name: '',
            is_active: true
        });
        setShowMerchandisingTaskModal(true);
    };

    return (
        <div className="container-fluid px-0">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

            {/* Header */}
            <div className="mb-4">
                <h3 style={{ color: '#010028', fontWeight: 'bold' }} className="mb-1">
                    {t('master_data_title', 'Módulo de Gestión de Datos Maestros')}
                </h3>
                <p className="text-muted mb-0">
                    {t('master_data_subtitle', 'Configuración centralizada de categorías, familias comerciales, cuentas receptoras de pagos y parámetros de visitas de campo.')}
                </p>
            </div>

            {/* Main Tabs Navigation */}
            <Tabs 
                activeKey={activeTab} 
                onSelect={(k) => setActiveTab(k)} 
                className="minimal-underline-tabs mb-4"
            >
                {/* 1. CATEGORÍAS Y SUBCATEGORÍAS DE PRODUCTOS */}
                <Tab eventKey="categories" title={<span><i className="mdi mdi-shape-outline me-1"></i> {t('tab_categories_short', 'Categorías y Subcategorías')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('categories_table_title', 'Categorías y Subcategorías del Catálogo')}</h5>
                                <small className="text-muted">{t('categories_table_subtitle', 'Estructura jerárquica de departamentos y subcategorías móviles con íconos Material Design.')}</small>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Button 
                                    size="sm" 
                                    variant="outline-primary"
                                    style={{ borderRadius: '6px', fontWeight: 'bold' }}
                                    onClick={() => openCategoryModal(null, rootCategories[0]?.id || '')}
                                >
                                    <i className="mdi mdi-plus-box-outline me-1"></i> {t('btn_new_subcategory', 'Nueva Subcategoría')}
                                </Button>
                                <Button 
                                    size="sm" 
                                    style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}
                                    onClick={() => openCategoryModal(null, '')}
                                >
                                    <i className="mdi mdi-folder-plus-outline me-1"></i> {t('btn_new_category', 'Nueva Categoría')}
                                </Button>
                            </div>
                        </Card.Header>

                        {/* FILTROS RÁPIDOS */}
                        <div className="px-4 py-3 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', backgroundColor: isDark ? '#121b33' : '#fafafa' }}>
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    onClick={() => setCategoryFilter('all')}
                                    className={`btn ${categoryFilter === 'all' ? 'btn-primary' : (isDark ? 'btn-outline-light' : 'btn-outline-secondary')}`}
                                    style={{ fontWeight: '600', borderRadius: '6px 0 0 6px' }}
                                >
                                    Todas ({categories.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCategoryFilter('root')}
                                    className={`btn ${categoryFilter === 'root' ? 'btn-primary' : (isDark ? 'btn-outline-light' : 'btn-outline-secondary')}`}
                                    style={{ fontWeight: '600' }}
                                >
                                    <i className="mdi mdi-folder me-1"></i> Categorías Principales ({rootCategories.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCategoryFilter('sub')}
                                    className={`btn ${categoryFilter === 'sub' ? 'btn-primary' : (isDark ? 'btn-outline-light' : 'btn-outline-secondary')}`}
                                    style={{ fontWeight: '600', borderRadius: '0 6px 6px 0' }}
                                >
                                    <i className="mdi mdi-subdirectory-arrow-right me-1"></i> Subcategorías ({subCategories.length})
                                </button>
                            </div>
                            <span className="text-muted small">
                                Mostrando <strong>{filteredCategories.length}</strong> registro(s)
                            </span>
                        </div>

                        <Card.Body className="p-0">
                            {loadingCategories ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3">{t('th_sku_prefix', 'Prefijo SKU')}</th>
                                            <th className="border-0 py-3 text-center" style={{ width: '80px' }}>{t('th_category_icon', 'Ícono')}</th>
                                            <th className="border-0 py-3">{t('th_category_name', 'Nombre')}</th>
                                            <th className="border-0 py-3">{t('th_hierarchy', 'Jerarquía / Categoría')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_catalog_status', 'Estado en Catálogo')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCategories.length > 0 ? filteredCategories.map(cat => (
                                            <tr key={cat.id}>
                                                <td className="px-4">
                                                    <span className="badge px-2 py-1" style={{ backgroundColor: 'rgba(24, 62, 235, 0.15)', color: '#183eeb', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                                        {cat.name}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    {cat.icono ? (
                                                        <div 
                                                            className="d-inline-flex align-items-center justify-content-center rounded-3"
                                                            style={{ 
                                                                width: '34px', 
                                                                height: '34px', 
                                                                backgroundColor: cat.icono_color ? `${cat.icono_color}1a` : (isDark ? 'rgba(24, 62, 235, 0.2)' : 'rgba(24, 62, 235, 0.1)'), 
                                                                color: cat.icono_color || cat.icon_color || '#183eeb',
                                                                border: `1px solid ${cat.icono_color ? `${cat.icono_color}33` : 'rgba(24, 62, 235, 0.2)'}`,
                                                                fontSize: '1.25rem'
                                                            }}
                                                            title={getCategoryIconMeta(cat.icono).label}
                                                        >
                                                            <i className={`mdi ${getCategoryIconMeta(cat.icono).mdi}`}></i>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted small" title="Sin ícono">—</span>
                                                    )}
                                                </td>
                                                <td><strong style={{ fontSize: '0.95rem' }}>{cat.value}</strong></td>
                                                <td>
                                                    {cat.parent_id ? (
                                                        <span 
                                                            className="badge" 
                                                            style={{ 
                                                                backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : '#f1f5f9', 
                                                                color: isDark ? '#cbd5e1' : '#475569', 
                                                                fontWeight: '600',
                                                                fontSize: '0.8rem',
                                                                padding: '0.4rem 0.65rem'
                                                            }}
                                                        >
                                                            <i className="mdi mdi-subdirectory-arrow-right me-1 text-primary"></i>
                                                            Subcategoría de: <strong>{cat.parent_name || 'Padre'}</strong>
                                                        </span>
                                                    ) : (
                                                        <span 
                                                            className="badge" 
                                                            style={{ 
                                                                backgroundColor: 'rgba(24, 62, 235, 0.12)', 
                                                                color: '#183eeb', 
                                                                fontWeight: '600',
                                                                fontSize: '0.8rem',
                                                                padding: '0.4rem 0.65rem'
                                                            }}
                                                        >
                                                            <i className="mdi mdi-folder me-1"></i>
                                                            Categoría Principal
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleToggleStatus('categories', cat)}
                                                        className={`btn btn-sm ${cat.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                        style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                        title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                    >
                                                        <i className={`mdi ${cat.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                        {cat.is_active ? t('status_visible', 'Visible') : t('status_hidden', 'Oculto')}
                                                    </button>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openCategoryModal(cat)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteRecord('categories', cat)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5 text-muted">
                                                    {t('no_categories_registered', 'No se encontraron registros en esta vista.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 2. FAMILIAS DE PRODUCTOS (MARCAS) */}
                <Tab eventKey="families" title={<span><i className="mdi mdi-buffer me-1"></i> {t('tab_families_short', 'Marcas / Familias')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('families_brands_title', 'Familias de Productos (Marcas Comerciales)')}</h5>
                                <small className="text-muted">{t('families_brands_subtitle', 'Gestión de marcas fabricantes y familias de productos (ej: Mavesa, Polar, Kraft).')}</small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px' }}
                                onClick={() => openFamilyModal()}
                            >
                                {t('btn_new_family', 'Nueva Familia')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingFamilies ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3" style={{ width: '80px' }}>{t('th_family_logo', 'Logo')}</th>
                                            <th className="border-0 py-3">{t('th_family_code', 'Código de Familia')}</th>
                                            <th className="border-0 py-3">{t('th_family_name', 'Nombre de la Familia (Marca)')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_status', 'Estado')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {families.length > 0 ? families.map(fam => (
                                            <tr key={fam.id}>
                                                <td className="px-4">
                                                    {fam.imagen_url ? (
                                                        <img 
                                                            src={fam.imagen_url} 
                                                            alt={fam.value} 
                                                            style={{ width: '42px', height: '42px', objectFit: 'contain', borderRadius: '8px', backgroundColor: '#ffffff', padding: '3px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} 
                                                        />
                                                    ) : (
                                                        <div 
                                                            style={{ width: '42px', height: '42px', borderRadius: '8px', backgroundColor: isDark ? '#233052' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1' }}
                                                            title={t('no_image', 'Sin logo')}
                                                        >
                                                            <i className="mdi mdi-image-outline" style={{ fontSize: '1.25rem' }}></i>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary" style={{ fontSize: '0.85rem' }}>{fam.name}</span>
                                                </td>
                                                <td><strong style={{ fontSize: '0.95rem' }}>{fam.value}</strong></td>
                                                <td className="text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleToggleStatus('brands', fam)}
                                                        className={`btn btn-sm ${fam.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                        style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                        title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                    >
                                                        <i className={`mdi ${fam.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                        {fam.is_active ? t('status_active', 'Activo') : t('status_inactive', 'Inactivo')}
                                                    </button>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openFamilyModal(fam)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteRecord('brands', fam)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5 text-muted">
                                                    {t('no_families_registered', 'No se encontraron familias de productos registradas.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 3. CUENTAS Y MÉTODOS DE PAGO */}
                <Tab eventKey="payments" title={<span><i className="mdi mdi-credit-card-outline me-1"></i> {t('tab_payments_short', 'Cuentas y Pagos')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('payment_methods_title', 'Cuentas y Pasarelas de Recaudación')}</h5>
                                <small className="text-muted">{t('payment_methods_subtitle', 'Configure los datos bancarios y de recaudación que visualizan los clientes en el checkout de la App Móvil.')}</small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px' }}
                                onClick={() => openPaymentModal()}
                            >
                                {t('btn_new_payment_method', 'Nueva Cuenta / Método')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingPayments ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3">{t('th_method_type', 'Tipo de Método')}</th>
                                            <th className="border-0 py-3">{t('th_account_title', 'Título y Canal')}</th>
                                            <th className="border-0 py-3">{t('th_bank_details', 'Datos Bancarios / Destino')}</th>
                                            <th className="border-0 py-3">{t('th_holder', 'Titular de la Cuenta')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_status', 'Estado')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentMethods.length > 0 ? paymentMethods.map(item => {
                                            const d = item.details || {};
                                            const type = d.type || (item.name.toLowerCase().includes('zelle') ? 'zelle' : (item.name.toLowerCase().includes('efectivo') ? 'efectivo' : (item.name.toLowerCase().includes('movil') ? 'pago_movil' : 'transferencia')));
                                            return (
                                                <tr key={item.id}>
                                                    <td className="px-4">
                                                        <Badge bg={type === 'pago_movil' ? 'primary' : (type === 'zelle' ? 'success' : (type === 'efectivo' ? 'dark' : 'info'))} style={{ fontSize: '0.85rem' }}>
                                                            {type === 'pago_movil' ? 'Pago Móvil' : (type === 'zelle' ? 'Zelle USD' : (type === 'efectivo' ? 'Efectivo' : 'Transferencia'))}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <strong style={{ fontSize: '0.95rem' }}>{d.title || item.name}</strong>
                                                        {d.subtitle && <div className="text-muted small">{d.subtitle}</div>}
                                                    </td>
                                                    <td>
                                                        {type === 'pago_movil' && (
                                                            <div style={{ fontSize: '0.85rem' }}>
                                                                <div><strong>Banco:</strong> {d.bank_name || '-'}</div>
                                                                <div><strong>RIF:</strong> {d.document || '-'} • <strong>Tlf:</strong> {d.phone || '-'}</div>
                                                            </div>
                                                        )}
                                                        {type === 'transferencia' && (
                                                            <div style={{ fontSize: '0.85rem' }}>
                                                                <div><strong>Banco:</strong> {d.bank_name || '-'}</div>
                                                                <div><strong>Cuenta:</strong> <code>{d.account_number || '-'}</code></div>
                                                            </div>
                                                        )}
                                                        {type === 'zelle' && (
                                                            <div style={{ fontSize: '0.85rem' }}>
                                                                <div><strong>Correo:</strong> <code>{d.email || '-'}</code></div>
                                                            </div>
                                                        )}
                                                        {type === 'efectivo' && (
                                                            <div style={{ fontSize: '0.85rem' }} className="text-muted">
                                                                {d.instructions || '-'}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{d.holder_name || '-'}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleToggleStatus('payment_methods', item)}
                                                            className={`btn btn-sm ${item.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                            style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                            title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                        >
                                                            <i className={`mdi ${item.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                            {item.is_active ? t('status_active', 'Activo') : t('status_inactive', 'Inactivo')}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 text-end">
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button 
                                                                type="button"
                                                                onClick={() => openPaymentModal(item)} 
                                                                className="btn btn-sm btn-outline-primary rounded-circle" 
                                                                style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                                title={t('btn_edit', 'Editar')}
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleDeleteRecord('payment_methods', item)} 
                                                                className="btn btn-sm btn-outline-danger rounded-circle" 
                                                                style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                                title={t('btn_delete', 'Eliminar')}
                                                            >
                                                                <i className="mdi mdi-delete"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5 text-muted">
                                                    {t('no_payment_methods_configured', 'No hay métodos de pago configurados.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 4. MOTIVOS DE NO VENTA */}
                <Tab eventKey="reasons" title={<span><i className="mdi mdi-alert-circle-outline me-1"></i> {t('tab_reasons_short', 'Motivos de No Venta')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('no_sale_reasons_title', 'Motivos de No Venta (Visitas en Calle)')}</h5>
                                <small className="text-muted">{t('no_sale_reasons_subtitle', 'Razones obligatorias para que el asesor comercial cierre visitas a clientes sin generar pedido.')}</small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px' }}
                                onClick={() => openReasonModal()}
                            >
                                <i className="mdi mdi-plus-circle-outline me-1"></i> {t('btn_new_no_sale_reason', 'Nuevo Motivo')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingReasons ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3">{t('th_reason_code', 'Código del Motivo')}</th>
                                            <th className="border-0 py-3">{t('th_reason_description', 'Descripción del Motivo')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_status', 'Estado')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {noSaleReasons.length > 0 ? noSaleReasons.map(reason => (
                                            <tr key={reason.id}>
                                                <td className="px-4">
                                                    <code>{reason.name}</code>
                                                </td>
                                                <td><strong style={{ fontSize: '0.95rem' }}>{reason.value}</strong></td>
                                                <td className="text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleToggleStatus('no_sale_reasons', reason)}
                                                        className={`btn btn-sm ${reason.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                        style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                        title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                    >
                                                        <i className={`mdi ${reason.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                        {reason.is_active ? t('status_active', 'Activo') : t('status_inactive', 'Inactivo')}
                                                    </button>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openReasonModal(reason)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteRecord('no_sale_reasons', reason)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted">
                                                    {t('no_reasons_registered', 'No se encontraron motivos de no venta registrados.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 4.1. MOTIVOS DE RECHAZO DE PAGO */}
                <Tab eventKey="payment_rejection_reasons" title={<span><i className="mdi mdi-close-octagon-outline me-1"></i> {t('tab_payment_rejection_reasons_short', 'Rechazos de Pago')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('payment_rejection_reasons_title', 'Motivos de Rechazo de Pagos (Cobranza y Conciliación)')}</h5>
                                <small className="text-muted">{t('payment_rejection_reasons_subtitle', 'Catálogo autogestionable de razones predeterminadas para rechazar comprobantes de pago en pedidos.')}</small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#dc3545', border: 'none', borderRadius: '6px' }}
                                onClick={() => openPaymentRejectionModal()}
                            >
                                <i className="mdi mdi-plus-circle-outline me-1"></i> {t('btn_new_payment_rejection_reason', 'Nuevo Motivo')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingPaymentRejectionReasons ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="danger" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3">{t('th_reason_code', 'Código Interno')}</th>
                                            <th className="border-0 py-3">{t('th_reason_description', 'Motivo Visible al Cliente')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_status', 'Estado')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentRejectionReasons.length > 0 ? paymentRejectionReasons.map(reason => (
                                            <tr key={reason.id}>
                                                <td className="px-4">
                                                    <code>{reason.name}</code>
                                                </td>
                                                <td><strong style={{ fontSize: '0.95rem' }}>{reason.value}</strong></td>
                                                <td className="text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleToggleStatus('payment_rejection_reasons', reason)}
                                                        className={`btn btn-sm ${reason.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                        style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                        title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                    >
                                                        <i className={`mdi ${reason.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                        {reason.is_active ? t('status_active', 'Activo') : t('status_inactive', 'Inactivo')}
                                                    </button>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openPaymentRejectionModal(reason)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteRecord('payment_rejection_reasons', reason)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted">
                                                    {t('no_rejection_reasons_registered', 'No se encontraron motivos de rechazo de pago registrados.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 4.2. LABORES DE APOYO (MERCADERISTA) */}
                <Tab eventKey="merchandising_tasks" title={<span><i className="mdi mdi-hand-heart-outline me-1"></i> {t('tab_merchandising_tasks_short', 'Labores de Apoyo')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('merchandising_tasks_title', 'Catálogo de Labores de Apoyo en Tienda')}</h5>
                                <small className="text-muted">{t('merchandising_tasks_subtitle', 'Actividades operativas seleccionables por mercaderistas durante las auditorías en punto de venta.')}</small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px' }}
                                onClick={() => openMerchandisingTaskModal()}
                            >
                                {t('btn_new_merchandising_task', '+ Nueva Labor')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingMerchandisingTasks ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', color: isDark ? '#94a3b8' : '#010028' }}>
                                        <tr>
                                            <th className="border-0 px-4 py-3">{t('th_task_code', 'Código de la Labor')}</th>
                                            <th className="border-0 py-3">{t('th_task_name', 'Descripción de la Labor')}</th>
                                            <th className="border-0 py-3 text-center">{t('th_status', 'Estado')}</th>
                                            <th className="border-0 px-4 py-3 text-end">{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {merchandisingTasks.length > 0 ? merchandisingTasks.map(task => (
                                            <tr key={task.id}>
                                                <td className="px-4">
                                                    <code>{task.name}</code>
                                                </td>
                                                <td><strong style={{ fontSize: '0.95rem' }}>{task.value}</strong></td>
                                                <td className="text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleToggleStatus('merchandising_tasks', task)}
                                                        className={`btn btn-sm ${task.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                                        style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                                        title={t('click_to_toggle_status', 'Clic para cambiar estado')}
                                                    >
                                                        <i className={`mdi ${task.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                                        {task.is_active ? t('status_active', 'Activo') : t('status_inactive', 'Inactivo')}
                                                    </button>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openMerchandisingTaskModal(task)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteRecord('merchandising_tasks', task)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted">
                                                    {t('no_tasks_registered', 'No se encontraron labores de apoyo registradas.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* 5. CONFIGURACIÓN GLOBAL */}
                <Tab eventKey="settings" title={<span><i className="mdi mdi-cog-outline me-1"></i> {t('tab_settings_short', 'Config. Global')}</span>}>
                    {loadingSettings ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">{t('loading', 'Cargando...')}</p>
                        </div>
                    ) : (
                        <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                            <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }}>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>{t('company_general_variables', 'Variables Generales de la Empresa')}</h5>
                                <small className="text-muted">{t('superadmin_only_hint', '(Requiere rol SuperAdmin)')}</small>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Form onSubmit={handleSaveSettings}>
                                    <Row className="g-3 align-items-start">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label 
                                                    className="d-flex align-items-center mb-2" 
                                                    style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', minHeight: '24px' }}
                                                >
                                                    {t('server_timezone', 'Zona Horaria del Servidor')}
                                                </Form.Label>
                                                <Form.Select 
                                                    value={settingsForm.timezone} 
                                                    onChange={e => setSettingsForm({...settingsForm, timezone: e.target.value})}
                                                    className="master-setting-select w-100"
                                                >
                                                    <option value="America/Caracas">America/Caracas (Caracas, Venezuela)</option>
                                                    <option value="America/Bogota">America/Bogota (Bogotá, Colombia)</option>
                                                    <option value="America/Mexico_City">America/Mexico_City (CDMX, México)</option>
                                                    <option value="America/Santiago">America/Santiago (Santiago, Chile)</option>
                                                    <option value="America/Lima">America/Lima (Lima, Perú)</option>
                                                    <option value="America/New_York">America/New_York (Nueva York, EEUU)</option>
                                                    <option value="UTC">UTC (Tiempo Universal)</option>
                                                </Form.Select>
                                                <Form.Text className="text-muted d-block mt-2" style={{ fontSize: '0.78rem', minHeight: '36px', lineHeight: '1.35' }}>
                                                    {t('timezone_help_text', 'Afecta el registro de horas de pesaje, visitas y transacciones de auditoría.')}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label 
                                                    className="d-flex align-items-center mb-2" 
                                                    style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', minHeight: '24px' }}
                                                >
                                                    {t('base_currency', 'Moneda Base del Sistema')}
                                                </Form.Label>
                                                <Form.Select 
                                                    value={settingsForm.currency} 
                                                    onChange={e => setSettingsForm({...settingsForm, currency: e.target.value})}
                                                    className="master-setting-select w-100"
                                                >
                                                    <option value="USD">USD ($ - Dólar Estadounidense)</option>
                                                    <option value="VES">VES (Bs - Bolívar Venezolano)</option>
                                                    <option value="COP">COP ($ - Peso Colombiano)</option>
                                                    <option value="EUR">EUR (€ - Euro)</option>
                                                </Form.Select>
                                                <Form.Text className="text-muted d-block mt-2" style={{ fontSize: '0.78rem', minHeight: '36px', lineHeight: '1.35' }}>
                                                    {t('currency_help_text', 'Divisa predeterminada para el cálculo de catálogo y pedidos.')}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label 
                                                    className="d-flex align-items-center mb-2" 
                                                    style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', minHeight: '24px' }}
                                                >
                                                    {t('tax_rate_label', 'Impuesto al Valor Agregado (IVA %)')}
                                                </Form.Label>
                                                <div className="master-setting-input-group">
                                                    <input 
                                                        type="number" 
                                                        min="0" 
                                                        max="100" 
                                                        step="0.01" 
                                                        value={settingsForm.iva_porcentaje} 
                                                        onChange={e => setSettingsForm({...settingsForm, iva_porcentaje: e.target.value})} 
                                                        placeholder="0.00" 
                                                        className="master-setting-control"
                                                        style={{ colorScheme: isDark ? 'dark' : 'light' }}
                                                    />
                                                    <div className="master-setting-addon-right">
                                                        %
                                                    </div>
                                                </div>
                                                <Form.Text className="text-muted d-block mt-2" style={{ fontSize: '0.78rem', minHeight: '36px', lineHeight: '1.35' }}>
                                                    {t('tax_rate_help_text', 'Ingrese 16 para aplicar 16%, u 8 para 8%. Ingrese 0 para omitir el IVA.')}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <hr className="my-4" style={{ borderColor: isDark ? '#233052' : '#e2e8f0' }} />

                                    <div className="mb-4">
                                        <div className="d-flex align-items-center mb-1">
                                            <div className="d-flex align-items-center justify-content-center rounded-3 me-3" style={{ width: '40px', height: '40px', backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#e0e7ff', color: '#183eeb' }}>
                                                <i className="mdi mdi-credit-card-settings-outline" style={{ fontSize: '1.4rem' }}></i>
                                            </div>
                                            <div>
                                                <h5 className="mb-0 fw-bold" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '1.1rem' }}>
                                                    {t('credit_terms_channels_title', 'Condiciones de Crédito y Políticas de Cobranza')}
                                                </h5>
                                                <small className="text-muted">
                                                    {t('credit_terms_channels_subtitle', 'Configuración de plazos de pago (días de crédito por factura), límites sugeridos y reglas de bloqueo por morosidad.')}
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Política de Bloqueo General */}
                                    <div 
                                        className="p-3 mb-4 rounded shadow-sm" 
                                        style={{ 
                                            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : '#fef2f2',
                                            border: isDark ? '1px solid #7f1d1d' : '1px solid #fecaca',
                                            borderLeft: '4px solid #ef4444'
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" 
                                                    style={{ width: '38px', height: '38px', backgroundColor: '#fee2e2', color: '#dc2626' }}
                                                >
                                                    <i className="mdi mdi-shield-alert-outline" style={{ fontSize: '1.3rem' }}></i>
                                                </div>
                                                <div>
                                                    <div className="fw-bold" style={{ color: isDark ? '#fca5a5' : '#991b1b', fontSize: '0.92rem' }}>
                                                        {t('credit_lock_policy_title', 'Regla de Bloqueo de Compras por Facturas Vencidas')}
                                                    </div>
                                                    <div className="text-muted small" style={{ fontSize: '0.78rem' }}>
                                                        {t('credit_lock_policy_desc', 'Número de facturas vencidas e impagas que causan el bloqueo automático de nuevos pedidos para el cliente.')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="small fw-semibold text-muted">{t('threshold_label', 'Bloquear al alcanzar:')}</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    className="form-control form-control-sm text-center fw-bold"
                                                    style={{ width: '80px', borderRadius: '8px', border: '1px solid #ef4444' }}
                                                    value={settingsForm.credit_settings?.max_facturas_vencidas_bloqueo ?? 2}
                                                    onChange={e => {
                                                        const val = Math.max(1, parseInt(e.target.value) || 1);
                                                        setSettingsForm(prev => ({
                                                            ...prev,
                                                            credit_settings: {
                                                                ...prev.credit_settings,
                                                                max_facturas_vencidas_bloqueo: val
                                                            }
                                                        }));
                                                    }}
                                                />
                                                <span className="small text-muted">{t('invoices_overdue_unit', 'facturas vencidas')}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <Row className="g-3 mb-4">
                                        {/* 1. Transformadores */}
                                        <Col lg={4} md={12}>
                                            <Card className="h-100 shadow-sm" style={{ 
                                                border: isDark ? '1px solid #7c2d12' : '1px solid #fed7aa',
                                                borderTop: '4px solid #ea580c',
                                                backgroundColor: isDark ? 'rgba(234, 88, 12, 0.04)' : '#ffffff',
                                                borderRadius: '12px' 
                                            }}>
                                                <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                                    <div>
                                                        {/* Header with Icon Avatar, Name & Badge */}
                                                        <div className="d-flex align-items-center justify-content-between mb-2 pb-1 gap-2">
                                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                                                                <div 
                                                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                                    style={{ 
                                                                        width: '36px', 
                                                                        height: '36px', 
                                                                        backgroundColor: isDark ? 'rgba(234, 88, 12, 0.15)' : '#fff7ed',
                                                                        color: isDark ? '#fb923c' : '#ea580c',
                                                                        border: isDark ? '1px solid rgba(234, 88, 12, 0.3)' : '1px solid #fed7aa'
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-silverware-fork-knife" style={{ fontSize: '1.2rem' }}></i>
                                                                </div>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.92rem', lineHeight: 1.25 }}>
                                                                        {t('channel_name_transformadores', 'Transformadores')}
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                            <span className="badge px-2 py-1 flex-shrink-0" style={{ 
                                                                backgroundColor: isDark ? 'rgba(234, 88, 12, 0.2)' : '#ffedd5', 
                                                                color: isDark ? '#fb923c' : '#c2410c', 
                                                                fontWeight: 600, 
                                                                fontSize: '0.67rem',
                                                                borderRadius: '6px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {t('channel_badge_b2b', 'Food Service / B2B')}
                                                            </span>
                                                        </div>

                                                        <div className="text-muted mb-3" style={{ fontSize: '0.75rem', minHeight: '34px', lineHeight: '1.35' }}>
                                                            {t('channel_desc_transformadores', 'Restaurantes, panaderías, pizzerías y hoteles')}
                                                        </div>

                                                        {/* Límite de Crédito Base ($) */}
                                                        <Form.Group className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_default_credit_limit', 'Límite de Crédito Base ($)')}
                                                                </Form.Label>
                                                                <span className="badge rounded-pill px-2 py-0" style={{ 
                                                                    backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9', 
                                                                    color: isDark ? '#94a3b8' : '#64748b', 
                                                                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', 
                                                                    fontSize: '0.67rem', 
                                                                    fontWeight: 500,
                                                                    lineHeight: '1.6'
                                                                }}>
                                                                    {t('badge_only_new_customers', 'Solo nuevos clientes')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    $
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="10"
                                                                    value={settingsForm.credit_settings?.canales?.transformadores?.limite_credito ?? 500}
                                                                    onChange={e => {
                                                                        const val = parseFloat(e.target.value) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    transformadores: {
                                                                                        ...prev.credit_settings?.canales?.transformadores,
                                                                                        limite_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    USD
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_limit_hint', 'Monto inicial asignado automáticamente al registrarse o segmentarse.')}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        {/* Días de Crédito (Plazo) */}
                                                        <Form.Group className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_credit_days', 'Días de Crédito por Factura')}
                                                                </Form.Label>
                                                                <span className="text-muted small" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
                                                                    {t('credit_days_term_short', 'Plazo de gracia')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    <i className="mdi mdi-calendar-range" style={{ fontSize: '1.1rem' }}></i>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="365"
                                                                    value={settingsForm.credit_settings?.canales?.transformadores?.dias_credito ?? 8}
                                                                    onChange={e => {
                                                                        const val = parseInt(e.target.value, 10) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    transformadores: {
                                                                                        ...prev.credit_settings?.canales?.transformadores,
                                                                                        dias_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    {t('credit_days_badge', 'días')}
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_days_hint', 'Días continuos antes del vencimiento de cada factura.')}
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* 2. Supermercados y Bodegones (Retail) */}
                                        <Col lg={4} md={12}>
                                            <Card className="h-100 shadow-sm" style={{ 
                                                border: isDark ? '1px solid #1e3a8a' : '1px solid #bfdbfe',
                                                borderTop: '4px solid #1d4ed8',
                                                backgroundColor: isDark ? 'rgba(29, 78, 216, 0.04)' : '#ffffff',
                                                borderRadius: '12px' 
                                            }}>
                                                <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                                    <div>
                                                        {/* Header with Icon Avatar, Name & Badge */}
                                                        <div className="d-flex align-items-center justify-content-between mb-2 pb-1 gap-2">
                                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                                                                <div 
                                                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                                    style={{ 
                                                                        width: '36px', 
                                                                        height: '36px', 
                                                                        backgroundColor: isDark ? 'rgba(29, 78, 216, 0.15)' : '#eff6ff',
                                                                        color: isDark ? '#60a5fa' : '#1d4ed8',
                                                                        border: isDark ? '1px solid rgba(29, 78, 216, 0.3)' : '1px solid #bfdbfe'
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-storefront-outline" style={{ fontSize: '1.2rem' }}></i>
                                                                </div>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.92rem', lineHeight: 1.25 }}>
                                                                        {t('channel_name_retail', 'Supermercados y Bodegones')}
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                            <span className="badge px-2 py-1 flex-shrink-0" style={{ 
                                                                backgroundColor: isDark ? 'rgba(29, 78, 216, 0.2)' : '#dbeafe', 
                                                                color: isDark ? '#60a5fa' : '#1e40af', 
                                                                fontWeight: 600, 
                                                                fontSize: '0.67rem',
                                                                borderRadius: '6px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {t('channel_badge_retail', 'Retail / Masivo')}
                                                            </span>
                                                        </div>

                                                        <div className="text-muted mb-3" style={{ fontSize: '0.75rem', minHeight: '34px', lineHeight: '1.35' }}>
                                                            {t('channel_desc_retail', 'Supermercados, hipermercados y bodegones')}
                                                        </div>

                                                        {/* Límite de Crédito Base ($) */}
                                                        <Form.Group className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_default_credit_limit', 'Límite de Crédito Base ($)')}
                                                                </Form.Label>
                                                                <span className="badge rounded-pill px-2 py-0" style={{ 
                                                                    backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9', 
                                                                    color: isDark ? '#94a3b8' : '#64748b', 
                                                                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', 
                                                                    fontSize: '0.67rem', 
                                                                    fontWeight: 500,
                                                                    lineHeight: '1.6'
                                                                }}>
                                                                    {t('badge_only_new_customers', 'Solo nuevos clientes')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    $
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="10"
                                                                    value={settingsForm.credit_settings?.canales?.supermercados_bodegones?.limite_credito ?? 1200}
                                                                    onChange={e => {
                                                                        const val = parseFloat(e.target.value) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    supermercados_bodegones: {
                                                                                        ...prev.credit_settings?.canales?.supermercados_bodegones,
                                                                                        limite_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    USD
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_limit_hint', 'Monto inicial asignado automáticamente al registrarse o segmentarse.')}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        {/* Días de Crédito (Plazo) */}
                                                        <Form.Group className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_credit_days', 'Días de Crédito por Factura')}
                                                                </Form.Label>
                                                                <span className="text-muted small" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
                                                                    {t('credit_days_term_short', 'Plazo de gracia')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    <i className="mdi mdi-calendar-range" style={{ fontSize: '1.1rem' }}></i>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="365"
                                                                    value={settingsForm.credit_settings?.canales?.supermercados_bodegones?.dias_credito ?? 8}
                                                                    onChange={e => {
                                                                        const val = parseInt(e.target.value, 10) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    supermercados_bodegones: {
                                                                                        ...prev.credit_settings?.canales?.supermercados_bodegones,
                                                                                        dias_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    {t('credit_days_badge', 'días')}
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_days_hint', 'Días continuos antes del vencimiento de cada factura.')}
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* 3. Tiendas y Bodegas (Canal Tradicional) */}
                                        <Col lg={4} md={12}>
                                            <Card className="h-100 shadow-sm" style={{ 
                                                border: isDark ? '1px solid #064e3b' : '1px solid #a7f3d0',
                                                borderTop: '4px solid #059669',
                                                backgroundColor: isDark ? 'rgba(5, 150, 105, 0.04)' : '#ffffff',
                                                borderRadius: '12px' 
                                            }}>
                                                <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                                    <div>
                                                        {/* Header with Icon Avatar, Name & Badge */}
                                                        <div className="d-flex align-items-center justify-content-between mb-2 pb-1 gap-2">
                                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                                                                <div 
                                                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                                    style={{ 
                                                                        width: '36px', 
                                                                        height: '36px', 
                                                                        backgroundColor: isDark ? 'rgba(5, 150, 105, 0.15)' : '#ecfdf5',
                                                                        color: isDark ? '#34d399' : '#059669',
                                                                        border: isDark ? '1px solid rgba(5, 150, 105, 0.3)' : '1px solid #a7f3d0'
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-store-outline" style={{ fontSize: '1.2rem' }}></i>
                                                                </div>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.92rem', lineHeight: 1.25 }}>
                                                                        {t('channel_name_tradicional', 'Tiendas y Bodegas')}
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                            <span className="badge px-2 py-1 flex-shrink-0" style={{ 
                                                                backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#d1fae5', 
                                                                color: isDark ? '#34d399' : '#065f46', 
                                                                fontWeight: 600, 
                                                                fontSize: '0.67rem',
                                                                borderRadius: '6px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {t('channel_badge_tradicional', 'Canal Tradicional')}
                                                            </span>
                                                        </div>

                                                        <div className="text-muted mb-3" style={{ fontSize: '0.75rem', minHeight: '34px', lineHeight: '1.35' }}>
                                                            {t('channel_desc_tradicional', 'Abastos, tiendas de conveniencia y bodegas')}
                                                        </div>

                                                        {/* Límite de Crédito Base ($) */}
                                                        <Form.Group className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_default_credit_limit', 'Límite de Crédito Base ($)')}
                                                                </Form.Label>
                                                                <span className="badge rounded-pill px-2 py-0" style={{ 
                                                                    backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9', 
                                                                    color: isDark ? '#94a3b8' : '#64748b', 
                                                                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', 
                                                                    fontSize: '0.67rem', 
                                                                    fontWeight: 500,
                                                                    lineHeight: '1.6'
                                                                }}>
                                                                    {t('badge_only_new_customers', 'Solo nuevos clientes')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    $
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="10"
                                                                    value={settingsForm.credit_settings?.canales?.tiendas_bodegas?.limite_credito ?? 300}
                                                                    onChange={e => {
                                                                        const val = parseFloat(e.target.value) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    tiendas_bodegas: {
                                                                                        ...prev.credit_settings?.canales?.tiendas_bodegas,
                                                                                        limite_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    USD
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_limit_hint', 'Monto inicial asignado automáticamente al registrarse o segmentarse.')}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        {/* Días de Crédito (Plazo) */}
                                                        <Form.Group className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.78rem' }}>
                                                                    {t('field_channel_credit_days', 'Días de Crédito por Factura')}
                                                                </Form.Label>
                                                                <span className="text-muted small" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
                                                                    {t('credit_days_term_short', 'Plazo de gracia')}
                                                                </span>
                                                            </div>
                                                            <div className="channel-credit-input-group">
                                                                <div className="channel-input-addon-left">
                                                                    <i className="mdi mdi-calendar-range" style={{ fontSize: '1.1rem' }}></i>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="365"
                                                                    value={settingsForm.credit_settings?.canales?.tiendas_bodegas?.dias_credito ?? 8}
                                                                    onChange={e => {
                                                                        const val = parseInt(e.target.value, 10) || 0;
                                                                        setSettingsForm(prev => ({
                                                                            ...prev,
                                                                            credit_settings: {
                                                                                ...prev.credit_settings,
                                                                                canales: {
                                                                                    ...prev.credit_settings?.canales,
                                                                                    tiendas_bodegas: {
                                                                                        ...prev.credit_settings?.canales?.tiendas_bodegas,
                                                                                        dias_credito: val
                                                                                    }
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="channel-input-control"
                                                                />
                                                                <div className="channel-input-addon-right">
                                                                    {t('credit_days_badge', 'días')}
                                                                </div>
                                                            </div>
                                                            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.71rem', lineHeight: '1.3' }}>
                                                                {t('channel_default_days_hint', 'Días continuos antes del vencimiento de cada factura.')}
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Propagation Banner Card */}
                                    <div 
                                        className="p-3 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3 shadow-sm" 
                                        style={{ 
                                            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
                                            border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                                            borderRadius: '12px'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-3" style={{ maxWidth: '850px' }}>
                                            <div 
                                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" 
                                                style={{ 
                                                    width: '42px', 
                                                    height: '42px', 
                                                    backgroundColor: settingsForm.propagate_to_existing_customers 
                                                        ? (isDark ? 'rgba(59, 130, 246, 0.25)' : '#e0e7ff') 
                                                        : (isDark ? 'rgba(148, 163, 184, 0.1)' : '#f1f5f9'),
                                                    color: settingsForm.propagate_to_existing_customers ? '#183eeb' : '#64748b',
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            >
                                                <i className="mdi mdi-sync" style={{ fontSize: '1.4rem' }}></i>
                                            </div>
                                            <div>
                                                <div className="fw-bold mb-1" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.95rem' }}>
                                                    {t('propagation_card_title', 'Actualizar días de crédito a todos los clientes existentes en cada canal')}
                                                </div>
                                                <div className="text-muted small" style={{ lineHeight: '1.4', fontSize: '0.8rem' }}>
                                                    {t('propagation_card_subtitle', 'Aplica los nuevos días de crédito a todas las cuentas ya registradas en cada canal. El límite de crédito modificado solo se asignará como valor predeterminado a nuevos clientes.')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 ms-auto">
                                            <Form.Check 
                                                type="switch"
                                                id="propagate_to_existing_customers"
                                                checked={settingsForm.propagate_to_existing_customers}
                                                onChange={e => setSettingsForm({ ...settingsForm, propagate_to_existing_customers: e.target.checked })}
                                                style={{ transform: 'scale(1.25)', cursor: 'pointer' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button 
                                            type="submit" 
                                            disabled={savingSettings}
                                            style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', padding: '10px 24px', fontWeight: 'bold' }}
                                        >
                                            {savingSettings ? t('loading', 'Guardando...') : t('btn_save_config', 'Guardar Configuración')}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}
                </Tab>

                {/* 6. BANNERS DEL INICIO */}
                <Tab eventKey="banners" title={<span><i className="mdi mdi-view-carousel-outline me-1"></i> {t('tab_home_banners', 'Banners de Inicio')}</span>}>
                    <Card className="mt-3" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Header style={{ backgroundColor: isDark ? '#10172d' : '#ffffff', borderBottom: isDark ? '1px solid #233052' : '1px solid #f0f0f0', borderRadius: '14px 14px 0 0', padding: '1.25rem 1.5rem' }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028' }}>
                                    {t('home_banners_title', 'Banners del Carrusel de Inicio')}
                                </h5>
                                <small className="text-muted">
                                    {t('home_banners_subtitle', 'Configuración de anuncios y promociones de la pantalla principal móvil (carrusel de clientes).')}
                                </small>
                            </div>
                            <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}
                                onClick={() => openBannerModal(null)}
                            >
                                <i className="mdi mdi-plus-box-outline me-1"></i> {t('btn_new_banner', 'Nuevo Banner')}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loadingBanners ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t('loading', 'Cargando banners...')}</p>
                                </div>
                            ) : banners.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="mdi mdi-view-carousel-outline" style={{ fontSize: '48px', color: '#cbd5e1' }}></i>
                                    <p className="mt-2 mb-0">{t('no_banners_found', 'No hay banners registrados en el sistema.')}</p>
                                    <small>{t('create_first_banner_hint', 'Haga clic en "Nuevo Banner" para crear la primera promoción.')}</small>
                                </div>
                            ) : (
                                <Table responsive hover className={`align-middle mb-0 ${isDark ? 'table-dark' : ''}`}>
                                    <thead style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ width: '85px', textAlign: 'center' }}>{t('th_order', 'Orden')}</th>
                                            <th style={{ minWidth: '220px' }}>{t('th_preview', 'Plantilla en la App')}</th>
                                            <th style={{ minWidth: '200px' }}>{t('th_content', 'Contenido')}</th>
                                            <th style={{ minWidth: '150px' }}>{t('th_action_target', 'Acción al Pulsar')}</th>
                                            <th style={{ width: '100px', textAlign: 'center' }}>{t('th_status', 'Estado')}</th>
                                            <th className="px-4 text-end" style={{ width: '120px' }}>{t('th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {banners.map((b, idx) => (
                                            <tr key={b.id || idx}>
                                                <td className="text-center">
                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                        <span className="badge" style={{ backgroundColor: '#183eeb', fontSize: '12px' }}>#{b.orden}</span>
                                                        <div className="d-flex flex-column">
                                                            <Button 
                                                                size="sm" 
                                                                variant="link" 
                                                                className="p-0 text-muted" 
                                                                disabled={idx === 0} 
                                                                onClick={() => handleMoveBanner(idx, 'up')}
                                                                style={{ lineHeight: 0.8, textDecoration: 'none' }}
                                                                title={t('tooltip_move_up', 'Subir posición')}
                                                            >
                                                                <i className="mdi mdi-chevron-up" style={{ fontSize: '18px' }}></i>
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="link" 
                                                                className="p-0 text-muted" 
                                                                disabled={idx === banners.length - 1} 
                                                                onClick={() => handleMoveBanner(idx, 'down')}
                                                                style={{ lineHeight: 0.8, textDecoration: 'none' }}
                                                                title={t('tooltip_move_down', 'Bajar posición')}
                                                            >
                                                                <i className="mdi mdi-chevron-down" style={{ fontSize: '18px' }}></i>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {/* Tarjeta Visual Idéntica a la App */}
                                                    <div style={{
                                                        background: `linear-gradient(135deg, ${b.bg_color_start || '#1E3A8A'} 0%, ${b.bg_color_end || '#3B82F6'} 100%)`,
                                                        borderRadius: '12px',
                                                        padding: '12px 14px',
                                                        color: b.text_color || '#ffffff',
                                                        maxWidth: '260px',
                                                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                                                        border: '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}>
                                                        <div style={{ fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.6px', color: b.text_color || '#ffffff', opacity: 0.85, textTransform: 'uppercase', marginBottom: '4px' }}>
                                                            {b.tag || 'OFERTAS'}
                                                        </div>
                                                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: b.text_color || '#ffffff', lineHeight: 1.25, marginBottom: '4px' }}>
                                                            {b.titulo}
                                                        </div>
                                                        {b.subtitulo && (
                                                            <div style={{ fontSize: '11px', color: b.text_color || '#ffffff', opacity: 0.85, lineHeight: 1.25, marginBottom: '8px' }}>
                                                                {b.subtitulo}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span 
                                                                className="banner-preview-text"
                                                                style={{ 
                                                                    display: 'inline-block',
                                                                    backgroundColor: b.btn_bg_color || '#183EEB', 
                                                                    color: `${b.btn_text_color || '#ffffff'} !important`, 
                                                                    fontSize: '10px', 
                                                                    fontWeight: 'bold', 
                                                                    padding: '3px 10px', 
                                                                    borderRadius: '5px',
                                                                    border: '1px solid rgba(255,255,255,0.2)'
                                                                }}
                                                            >
                                                                {b.action_label || 'Ver ahora'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {b.tag && (
                                                        <span className="badge rounded-pill mb-1" style={{ backgroundColor: '#e0e7ff', color: '#3730a3', fontSize: '11px', fontWeight: '600' }}>
                                                            {b.tag}
                                                        </span>
                                                    )}
                                                    <div className="fw-bold" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '14px' }}>
                                                        {b.titulo}
                                                    </div>
                                                    {b.subtitulo && (
                                                        <small className="text-muted d-block mt-1" style={{ fontSize: '12px' }}>
                                                            {b.subtitulo}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>
                                                    {b.action_type === 'category' && (
                                                        <span className="badge" style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-shape-outline me-1"></i> {t('action_category', 'Categoría')}: {b.category?.nombre || 'Seleccionada'}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'brand' && (
                                                        <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-tag-outline me-1"></i> {t('action_brand', 'Marca')}: {families.find(f => f.name === b.target_id || f.id === b.target_id)?.value || b.target_id || 'Marca'}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'product' && (
                                                        <span className="badge" style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-package-variant-closed me-1"></i> {t('action_product', 'Producto')}: {catalogProducts.find(p => p.id === b.target_id)?.nombre || b.target_id || 'Producto'}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'promotions' && (
                                                        <span className="badge" style={{ backgroundColor: '#ffedd5', color: '#c2410c', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-percent-outline me-1"></i> {t('action_promotions', 'Promociones / Ofertas')}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'newest' && (
                                                        <span className="badge" style={{ backgroundColor: '#e0e7ff', color: '#3730a3', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-star-outline me-1"></i> {t('action_newest', 'Novedades')}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'invoices' && (
                                                        <span className="badge" style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-file-document-outline me-1"></i> {t('action_invoices', 'Mis Facturas')}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'url' && (
                                                        <span className="badge" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-link-variant me-1"></i> {t('action_url', 'Enlace Web / WhatsApp')}
                                                        </span>
                                                    )}
                                                    {b.action_type === 'custom_deeplink' && (
                                                        <span className="badge" style={{ backgroundColor: '#f1f5f9', color: '#334155', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-code-tags me-1"></i> {t('action_custom_deeplink', 'Deeplink / Ruta Personalizada')}
                                                        </span>
                                                    )}
                                                    {(b.action_type === 'catalog' || !b.action_type) && (
                                                        <span className="badge" style={{ backgroundColor: '#eff6ff', color: '#1e40af', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' }}>
                                                            <i className="mdi mdi-book-open-outline me-1"></i> {t('action_catalog', 'Catálogo General')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Form.Check 
                                                        type="switch"
                                                        id={`switch-banner-${b.id}`}
                                                        checked={b.is_active}
                                                        onChange={() => handleToggleBannerStatus(b)}
                                                        label=""
                                                        style={{ cursor: 'pointer', display: 'inline-block' }}
                                                    />
                                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: b.is_active ? '#10b981' : '#94a3b8' }}>
                                                        {b.is_active ? t('active', 'Activo') : t('inactive', 'Inactivo')}
                                                    </div>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => openBannerModal(b)} 
                                                            className="btn btn-sm btn-outline-primary rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_edit', 'Editar')}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteBanner(b)} 
                                                            className="btn btn-sm btn-outline-danger rounded-circle" 
                                                            style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                                                            title={t('btn_delete', 'Eliminar')}
                                                        >
                                                            <i className="mdi mdi-delete"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* MODAL CATEGORÍA / SUBCATEGORÍA */}
            <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {categoryForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {categoryForm.parent_id ? 'Subcategoría' : t('tab_categories', 'Categoría de Productos')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveCategory}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_category_hierarchy', 'Jerarquía / Categoría Padre')}</Form.Label>
                            <Form.Select 
                                value={categoryForm.parent_id || ''} 
                                onChange={e => setCategoryForm({...categoryForm, parent_id: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            >
                                <option value="">📁 Es una Categoría Principal (Sin categoría padre)</option>
                                {rootCategories.filter(r => r.id !== categoryForm.id).map(r => (
                                    <option key={r.id} value={r.id}>
                                        ↳ Subcategoría de: {r.value}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted small">
                                {categoryForm.parent_id ? 'Este elemento figurará como subcategoría dentro del departamento padre en el catálogo móvil.' : 'Este elemento figurará como categoría principal (departamento raíz).'}
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_sku_prefix', 'Prefijo SKU (3 letras)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                maxLength={5}
                                placeholder={t('placeholder_sku_prefix', 'Ej: LAC, EMB, BEB, VIV')}
                                value={categoryForm.prefijo_sku}
                                onChange={e => setCategoryForm({...categoryForm, prefijo_sku: e.target.value.toUpperCase()})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                {categoryForm.parent_id ? 'Nombre de la Subcategoría' : t('th_category_name', 'Nombre de la Categoría')}
                            </Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={categoryForm.parent_id ? 'Ej: Carnes Procesadas, Lácteos, Caldos' : t('placeholder_category_name', 'Ej: Lácteos y Derivados, Víveres')}
                                value={categoryForm.nombre}
                                onChange={e => setCategoryForm({...categoryForm, nombre: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                {categoryForm.parent_id ? 'Ícono de la Subcategoría (Material Design)' : t('th_category_icon', 'Ícono de la Categoría (Material Design)')}
                            </Form.Label>
                            <CategoryIconPicker
                                value={categoryForm.icono}
                                onChange={iconKey => setCategoryForm({...categoryForm, icono: iconKey})}
                                isDark={isDark}
                            />
                        </Form.Group>

                        {/* COLOR PERSONALIZADO DEL ÍCONO */}
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                {t('field_category_icon_color', 'Color Personalizado del Ícono')}
                            </Form.Label>
                            <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ backgroundColor: isDark ? '#1a233a' : '#f8f9fa', border: isDark ? '1px solid #2e3a59' : '1px solid #e2e8f0' }}>
                                {/* Preview del Ícono con el Color */}
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 shadow-sm"
                                    style={{ 
                                        width: '54px', 
                                        height: '54px', 
                                        backgroundColor: `${categoryForm.icono_color || '#183EEB'}1a`, 
                                        color: categoryForm.icono_color || '#183EEB',
                                        border: `2px solid ${categoryForm.icono_color || '#183EEB'}40`,
                                        fontSize: '1.9rem'
                                    }}
                                    title="Previsualización del ícono con el color seleccionado"
                                >
                                    <i className={`mdi ${getCategoryIconMeta(categoryForm.icono).mdi}`}></i>
                                </div>

                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <input
                                            type="color"
                                            value={categoryForm.icono_color || '#183EEB'}
                                            onChange={e => setCategoryForm({...categoryForm, icono_color: e.target.value.toUpperCase()})}
                                            style={{
                                                width: '42px',
                                                height: '38px',
                                                padding: '2px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                cursor: 'pointer'
                                            }}
                                            title="Seleccionar color personalizado"
                                        />
                                        <input
                                            type="text"
                                            maxLength={7}
                                            value={categoryForm.icono_color || '#183EEB'}
                                            onChange={e => setCategoryForm({...categoryForm, icono_color: e.target.value.toUpperCase()})}
                                            className="form-control form-control-sm text-uppercase fw-bold"
                                            style={{ width: '110px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
                                            placeholder="#183EEB"
                                        />
                                        <span className="small text-muted">
                                            {t('color_preview_label', 'Color asignado para el catálogo móvil')}
                                        </span>
                                    </div>

                                    {/* Paleta de colores rápidos */}
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="small text-muted me-1" style={{ fontSize: '0.75rem' }}>Sugeridos:</span>
                                        {[
                                            { name: 'Azul Rutex', hex: '#183EEB' },
                                            { name: 'Azul Marino', hex: '#00024F' },
                                            { name: 'Esmeralda', hex: '#10B981' },
                                            { name: 'Verde Hoja', hex: '#059669' },
                                            { name: 'Ámbar / Naranja', hex: '#F59E0B' },
                                            { name: 'Naranja Fuerte', hex: '#EA580C' },
                                            { name: 'Rojo Carmesí', hex: '#EF4444' },
                                            { name: 'Rosa / Fresa', hex: '#F43F5E' },
                                            { name: 'Púrpura', hex: '#8B5CF6' },
                                            { name: 'Teal / Cian', hex: '#06B6D4' },
                                            { name: 'Gris Neutro', hex: '#64748B' },
                                        ].map(palette => (
                                            <button
                                                key={palette.hex}
                                                type="button"
                                                onClick={() => setCategoryForm({...categoryForm, icono_color: palette.hex})}
                                                className="rounded-circle border-0 p-0"
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    backgroundColor: palette.hex,
                                                    boxShadow: (categoryForm.icono_color || '#183EEB').toUpperCase() === palette.hex ? `0 0 0 3px #ffffff, 0 0 0 5px ${palette.hex}` : 'none',
                                                    transform: (categoryForm.icono_color || '#183EEB').toUpperCase() === palette.hex ? 'scale(1.15)' : 'scale(1)',
                                                    transition: 'all 0.15s ease-in-out',
                                                    cursor: 'pointer'
                                                }}
                                                title={palette.name}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowCategoryModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingCategory} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingCategory ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL FAMILIA (MARCA) */}
            <Modal show={showFamilyModal} onHide={() => setShowFamilyModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {familyForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('tab_families_brands', 'Familia de Productos (Marca)')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveFamily}>
                    <Modal.Body>
                        {/* LOGO / IMAGEN DE MARCA */}
                        <Form.Group className="mb-4">
                            <Form.Label style={{ color: isDark ? '#e2e8f0' : '#010028', fontWeight: 'bold' }}>
                                {t('field_brand_logo', 'Logo o Imagen de la Marca')}
                            </Form.Label>
                            <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ backgroundColor: isDark ? '#1a233a' : '#f8f9fa', border: isDark ? '1px solid #2e3a59' : '1px solid #e2e8f0' }}>
                                {familyForm.imagen_url ? (
                                    <div className="position-relative">
                                        <img 
                                            src={familyForm.imagen_url} 
                                            alt="Logo Preview" 
                                            style={{ width: '70px', height: '70px', objectFit: 'contain', borderRadius: '10px', backgroundColor: '#ffffff', padding: '5px', border: '1px solid #cbd5e1' }} 
                                        />
                                    </div>
                                ) : (
                                    <div style={{ width: '70px', height: '70px', borderRadius: '10px', backgroundColor: isDark ? '#233052' : '#e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', border: '2px dashed #94a3b8' }}>
                                        <i className="mdi mdi-cloud-upload-outline" style={{ fontSize: '1.6rem' }}></i>
                                    </div>
                                )}
                                <div className="flex-grow-1">
                                    <input 
                                        type="file" 
                                        ref={brandFileInputRef} 
                                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" 
                                        onChange={handleBrandImageUpload} 
                                        style={{ display: 'none' }} 
                                    />
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Button 
                                            type="button" 
                                            size="sm" 
                                            variant="outline-primary" 
                                            disabled={uploadingBrandImage}
                                            onClick={() => brandFileInputRef.current?.click()}
                                            style={{ borderRadius: '8px', fontWeight: '600' }}
                                        >
                                            {uploadingBrandImage ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    {t('uploading', 'Subiendo al bucket...')}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="mdi mdi-upload me-1"></i>
                                                    {familyForm.imagen_url ? t('btn_change_logo', 'Cambiar Logo') : t('btn_upload_logo', 'Subir Logo / Imagen')}
                                                </>
                                            )}
                                        </Button>
                                        {familyForm.imagen_url && (
                                            <Button 
                                                type="button" 
                                                size="sm" 
                                                variant="outline-danger" 
                                                disabled={uploadingBrandImage}
                                                onClick={() => setFamilyForm({ ...familyForm, imagen_url: '' })}
                                                style={{ borderRadius: '8px' }}
                                                title={t('btn_remove_logo', 'Quitar logo')}
                                            >
                                                <i className="mdi mdi-trash-can-outline me-1"></i>
                                                {t('btn_remove', 'Quitar')}
                                            </Button>
                                        )}
                                    </div>
                                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.78rem' }}>
                                        {t('brand_logo_hint', 'Formatos: PNG, JPG, WebP o SVG. Se guardará directamente en el bucket S3 / DigitalOcean Spaces.')}
                                    </small>
                                </div>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_family_code', 'Código Interno (Mayúsculas)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_family_code', 'Ej: MAVESA, POLAR, KRAFT')}
                                value={familyForm.code}
                                onChange={e => setFamilyForm({...familyForm, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_family_name', 'Nombre de la Familia (Marca)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_family_name', 'Ej: Mavesa, Alimentos Polar, Kraft')}
                                value={familyForm.name}
                                onChange={e => setFamilyForm({...familyForm, name: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowFamilyModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingFamily} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingFamily ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL MÉTODO DE PAGO */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {paymentForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('modal_payment_method_title', 'Configurar Cuenta / Método de Pago')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSavePaymentMethod}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_payment_type', 'Tipo de Método')}</Form.Label>
                                    <Form.Select 
                                        value={paymentForm.type}
                                        onChange={e => {
                                            const type = e.target.value;
                                            setPaymentForm({
                                                ...paymentForm,
                                                type: type,
                                                code: type.toUpperCase(),
                                                currency: (type === 'zelle' || type === 'efectivo') ? 'USD' : 'VES'
                                            });
                                        }}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    >
                                        <option value="pago_movil">{t('type_pago_movil', 'Pago Móvil')}</option>
                                        <option value="transferencia">{t('type_transfer', 'Transferencia Bancaria')}</option>
                                        <option value="zelle">{t('type_zelle', 'Zelle (USD)')}</option>
                                        <option value="efectivo">{t('type_cash', 'Efectivo / Divisas')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_payment_currency', 'Moneda')}</Form.Label>
                                    <Form.Select 
                                        value={paymentForm.currency}
                                        onChange={e => setPaymentForm({...paymentForm, currency: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    >
                                        <option value="VES">VES (Bolívares)</option>
                                        <option value="USD">USD (Dólares)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_method_title', 'Título en Pantalla')}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        required 
                                        placeholder={t('placeholder_method_title', 'Ej: Pago Móvil, Transferencia Banesco')}
                                        value={paymentForm.title}
                                        onChange={e => setPaymentForm({...paymentForm, title: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_method_subtitle', 'Subtítulo / Etiqueta')}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder={t('placeholder_method_subtitle', 'Ej: Instantáneo • Banco de Venezuela')}
                                        value={paymentForm.subtitle}
                                        onChange={e => setPaymentForm({...paymentForm, subtitle: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Campos dinámicos según el tipo */}
                        {(paymentForm.type === 'pago_movil' || paymentForm.type === 'transferencia') && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_bank_name', 'Banco y Código')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_bank_name', 'Ej: 0102 Banco de Venezuela')}
                                            value={paymentForm.bank_name}
                                            onChange={e => setPaymentForm({...paymentForm, bank_name: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_document_id', 'Documento / RIF')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_document_id', 'Ej: J-012345678-9')}
                                            value={paymentForm.document}
                                            onChange={e => setPaymentForm({...paymentForm, document: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {paymentForm.type === 'pago_movil' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_phone_number', 'Teléfono de Pago Móvil')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_phone_number', 'Ej: 0412-1234567')}
                                            value={paymentForm.phone}
                                            onChange={e => setPaymentForm({...paymentForm, phone: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_account_holder', 'Titular de la Cuenta')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_account_holder', 'Ej: Rutex Logistics C.A.')}
                                            value={paymentForm.holder_name}
                                            onChange={e => setPaymentForm({...paymentForm, holder_name: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {paymentForm.type === 'transferencia' && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_account_number', 'Número de Cuenta (20 dígitos)')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            maxLength={20}
                                            placeholder={t('placeholder_account_number', 'Ej: 01020123456789012345')}
                                            value={paymentForm.account_number}
                                            onChange={e => setPaymentForm({...paymentForm, account_number: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_account_holder', 'Titular de la Cuenta')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_account_holder', 'Ej: Rutex Logistics C.A.')}
                                            value={paymentForm.holder_name}
                                            onChange={e => setPaymentForm({...paymentForm, holder_name: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {paymentForm.type === 'zelle' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_zelle_email', 'Correo Electrónico Zelle')}</Form.Label>
                                        <Form.Control 
                                            type="email" 
                                            placeholder={t('placeholder_zelle_email', 'Ej: pagos@empresa.com')}
                                            value={paymentForm.email}
                                            onChange={e => setPaymentForm({...paymentForm, email: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_account_holder', 'Titular de la Cuenta')}</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t('placeholder_account_holder', 'Ej: Rutex Global LLC')}
                                            value={paymentForm.holder_name}
                                            onChange={e => setPaymentForm({...paymentForm, holder_name: e.target.value})}
                                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_payment_instructions', 'Instrucciones para el Cliente')}</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={2}
                                placeholder={t('placeholder_instructions', 'Ej: Una vez realizado el pago, capture el comprobante. Deberá adjuntar el número de referencia.')}
                                value={paymentForm.instructions}
                                onChange={e => setPaymentForm({...paymentForm, instructions: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowPaymentModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingPayment} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingPayment ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL MOTIVO DE NO VENTA */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {reasonForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('tab_no_sale_reasons', 'Motivo de No Venta')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveReason}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_reason_code', 'Código Interno (Mayúsculas)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_reason_code', 'Ej: LOCAL_CERRADO, SIN_LIQUIDEZ')}
                                value={reasonForm.code}
                                onChange={e => setReasonForm({...reasonForm, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_reason_description', 'Descripción del Motivo')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_reason_description', 'Ej: Local o establecimiento comercial cerrado')}
                                value={reasonForm.name}
                                onChange={e => setReasonForm({...reasonForm, name: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowReasonModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingReason} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingReason ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL MOTIVO DE RECHAZO DE PAGO */}
            <Modal show={showPaymentRejectionModal} onHide={() => setShowPaymentRejectionModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {paymentRejectionForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('tab_payment_rejection_reason', 'Motivo de Rechazo de Pago')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSavePaymentRejection}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_reason_code', 'Código Interno (Mayúsculas)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_payment_rejection_code', 'Ej: REFERENCIA_INVALIDA, MONTO_INCORRECTO')}
                                value={paymentRejectionForm.code}
                                onChange={e => setPaymentRejectionForm({...paymentRejectionForm, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                                style={{ border: '1px solid #dc3545', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_reason_description', 'Motivo Visible al Cliente')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_payment_rejection_desc', 'Ej: Número de referencia no encontrado en extracto bancario')}
                                value={paymentRejectionForm.name}
                                onChange={e => setPaymentRejectionForm({...paymentRejectionForm, name: e.target.value})}
                                style={{ border: '1px solid #dc3545', borderRadius: '10px' }}
                            />
                            <Form.Text className="text-muted">
                                Este es el texto que se le mostrará al cliente en la notificación push, correo y app móvil.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowPaymentRejectionModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingPaymentRejection} style={{ backgroundColor: '#dc3545', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingPaymentRejection ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL BANNER DE INICIO */}
            <Modal show={showBannerModal} onHide={() => setShowBannerModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {bannerForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('modal_banner_title', 'Banner de Inicio')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveBanner}>
                    <Modal.Body>
                        <Row>
                            {/* Columna Izquierda: Formulario de Campos */}
                            <Col md={7}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                        {t('field_banner_tag', 'Etiqueta Superior (Tag / Badge)')}
                                    </Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder={t('placeholder_banner_tag', 'Ej: OFERTAS DE LA SEMANA, LOGÍSTICA, DESTACADO')}
                                        value={bannerForm.tag}
                                        onChange={e => setBannerForm({...bannerForm, tag: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Texto pequeño superior sobre el título (opcional).
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                        {t('field_banner_title', 'Título Principal')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        required 
                                        placeholder={t('placeholder_banner_title', 'Ej: DESPACHO GARANTIZADO 24H')}
                                        value={bannerForm.titulo}
                                        onChange={e => setBannerForm({...bannerForm, titulo: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                        {t('field_banner_subtitle', 'Subtítulo / Mensaje Secundario')}
                                    </Form.Label>
                                    <Form.Control 
                                        as="textarea"
                                        rows={2}
                                        placeholder={t('placeholder_banner_subtitle', 'Ej: Envíos directos a tu comercio en tiempo récord')}
                                        value={bannerForm.subtitulo}
                                        onChange={e => setBannerForm({...bannerForm, subtitulo: e.target.value})}
                                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                                {t('field_banner_action_label', 'Texto del Botón')}
                                            </Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                placeholder="Ver ahora"
                                                value={bannerForm.action_label}
                                                onChange={e => setBannerForm({...bannerForm, action_label: e.target.value})}
                                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                                {t('field_banner_order', 'Posición en el Carrusel')}
                                            </Form.Label>
                                            <Form.Select 
                                                value={bannerForm.orden}
                                                onChange={e => setBannerForm({...bannerForm, orden: parseInt(e.target.value, 10) || 1})}
                                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                            >
                                                {Array.from({ length: bannerForm.id ? banners.length : banners.length + 1 }, (_, i) => i + 1).map(num => (
                                                    <option key={num} value={num}>
                                                        {num === 1 ? '1 (Primero en mostrarse)' : `${num}${num === (bannerForm.id ? banners.length : banners.length + 1) ? ' (Última posición)' : ''}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Si eliges una posición ya ocupada, los demás se desplazan automáticamente.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" style={{ opacity: 0.15 }} />

                                {/* SECCIÓN: ACCIÓN AL PULSAR */}
                                <h6 className="fw-bold mb-2" style={{ color: '#183eeb' }}>
                                    <i className="mdi mdi-cursor-pointer me-1"></i> Acción y Destino al Pulsar
                                </h6>
                                <Row>
                                    <Col md={bannerForm.action_type === 'catalog' || bannerForm.action_type === 'invoices' ? 12 : 6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                                {t('field_banner_action_type', 'Tipo de Acción')}
                                            </Form.Label>
                                            <Form.Select 
                                                value={bannerForm.action_type}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setBannerForm({
                                                        ...bannerForm, 
                                                        action_type: val,
                                                        target_id: (val === 'catalog' || val === 'invoices') ? '' : bannerForm.target_id
                                                    });
                                                    if (val === 'product' && catalogProducts.length === 0) fetchCatalogProducts();
                                                    if (val === 'brand' && families.length === 0) fetchFamilies();
                                                }}
                                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                            >
                                                <option value="catalog">Catálogo General (Todos los productos)</option>
                                                <option value="category">Por Categoría (Elegir categoría)</option>
                                                <option value="brand">Por Marca / Familia (Elegir marca)</option>
                                                <option value="product">Producto Específico (Elegir producto)</option>
                                                <option value="invoices">Mis Facturas (Facturas y cobranzas)</option>
                                                <option value="custom_deeplink">Deeplink / Ruta Personalizada (Avanzado)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {bannerForm.action_type === 'category' && (
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                                    {t('field_banner_category', 'Categoría Destino')} <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Select 
                                                    required
                                                    value={bannerForm.target_id}
                                                    onChange={e => setBannerForm({...bannerForm, target_id: e.target.value})}
                                                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                                >
                                                    <option value="">Seleccione una categoría...</option>
                                                    {categories.map(c => (
                                                        <option key={c.id} value={c.id}>{c.value || c.nombre}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    )}

                                    {bannerForm.action_type === 'brand' && (
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                                                    Marca / Familia Destino <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Select 
                                                    required
                                                    value={bannerForm.target_id}
                                                    onChange={e => setBannerForm({...bannerForm, target_id: e.target.value})}
                                                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                                >
                                                    <option value="">Seleccione una marca...</option>
                                                    {families.map(f => (
                                                        <option key={f.id} value={f.name || f.id}>{f.value} ({f.name})</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    )}

                                    {bannerForm.action_type === 'product' && (
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <Form.Label className="mb-0" style={{ color: '#010028', fontWeight: 'bold' }}>
                                                        Producto Destino <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                                                        {filteredProductsForAction.length} disponibles
                                                    </span>
                                                </div>
                                                <Row className="g-1 mb-2">
                                                    <Col md={12}>
                                                        <Form.Select
                                                            size="sm"
                                                            value={productCategoryFilter}
                                                            onChange={e => {
                                                                setProductCategoryFilter(e.target.value);
                                                                setBannerForm(bf => ({ ...bf, target_id: '' }));
                                                            }}
                                                            style={{ borderRadius: '6px', fontSize: '11px', borderColor: '#cbd5e1' }}
                                                        >
                                                            <option value="">Filtrar productos por categoría (Todas)...</option>
                                                            {categories.map(c => (
                                                                <option key={c.id} value={c.id}>{c.value || c.nombre}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Col>
                                                </Row>
                                                <Form.Select 
                                                    required
                                                    value={bannerForm.target_id}
                                                    onChange={e => setBannerForm({...bannerForm, target_id: e.target.value})}
                                                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                                                >
                                                    <option value="">Seleccione un producto...</option>
                                                    {filteredProductsForAction.map(p => {
                                                        const stock = Number(p.stock_real) || 0;
                                                        const stockLabel = stock > 0 ? `[Stock: ${stock}]` : '[Agotado]';
                                                        return (
                                                            <option key={p.id} value={p.id}>
                                                                [{p.sku}] {p.nombre} {p.marca ? `(${p.marca})` : ''} - {stockLabel}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    )}

                                    {bannerForm.action_type === 'custom_deeplink' && (
                                        <Col md={12}>
                                            <Card className="p-3 mb-3 border" style={{ backgroundColor: isDark ? '#151c33' : '#f8fafd', borderRadius: '10px' }}>
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                                    <span className="fw-bold" style={{ fontSize: '13px', color: '#183eeb' }}>
                                                        <i className="mdi mdi-auto-fix me-1"></i> Asistente de Filtros y Rutas Guiadas
                                                    </span>
                                                    <ButtonGroup size="sm">
                                                        <Button 
                                                            variant={deeplinkBuilder.mode === 'catalog' ? 'primary' : 'outline-secondary'} 
                                                            style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px 0 0 6px' }}
                                                            onClick={() => updateDeeplinkBuilder({ mode: 'catalog' })}
                                                        >
                                                            <i className="mdi mdi-filter-variant me-1"></i> Filtros de Catálogo
                                                        </Button>
                                                        <Button 
                                                            variant={deeplinkBuilder.mode === 'screen' ? 'primary' : 'outline-secondary'} 
                                                            style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '0 6px 6px 0' }}
                                                            onClick={() => updateDeeplinkBuilder({ mode: 'screen' })}
                                                        >
                                                            <i className="mdi mdi-cellphone-link me-1"></i> Pantallas del Sistema
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>

                                                {deeplinkBuilder.mode === 'catalog' && (
                                                    <div>
                                                        <Row className="mb-2">
                                                            <Col md={6}>
                                                                <Form.Group>
                                                                    <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Filtrar por Categoría (Opcional)</Form.Label>
                                                                    <Form.Select 
                                                                        size="sm"
                                                                        value={deeplinkBuilder.categoryId}
                                                                        onChange={e => {
                                                                            const newCat = e.target.value;
                                                                            updateDeeplinkBuilder(prev => {
                                                                                // Si la marca previamente elegida no pertenece a la nueva categoría, resetearla
                                                                                let resetBrand = prev.brandId;
                                                                                if (newCat && resetBrand) {
                                                                                    const brandsInNewCat = new Set(
                                                                                        catalogProducts
                                                                                            .filter(p => p.categoria_id === newCat || p.subcategory_id === newCat)
                                                                                            .map(p => (p.marca || '').toUpperCase().trim())
                                                                                    );
                                                                                    if (!brandsInNewCat.has(resetBrand.toUpperCase().trim())) {
                                                                                        resetBrand = '';
                                                                                    }
                                                                                }
                                                                                return { ...prev, categoryId: newCat, brandId: resetBrand };
                                                                            });
                                                                        }}
                                                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                                                    >
                                                                        <option value="">Todas las categorías</option>
                                                                        {categories.map(c => (
                                                                            <option key={c.id} value={c.id}>{c.value || c.nombre}</option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Form.Group>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Filtrar por Marca (Opcional)</Form.Label>
                                                                        {deeplinkBuilder.categoryId && (
                                                                            <span className="text-muted" style={{ fontSize: '10px' }}>
                                                                                {availableBrandsForSelectedCategory.length} marcas en esta categoría
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <Form.Select 
                                                                        size="sm"
                                                                        value={deeplinkBuilder.brandId}
                                                                        onChange={e => updateDeeplinkBuilder({ brandId: e.target.value })}
                                                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                                                    >
                                                                        <option value="">Todas las marcas</option>
                                                                        {availableBrandsForSelectedCategory.map(f => (
                                                                            <option key={f.id} value={f.name || f.id}>{f.value} ({f.name})</option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Row className="mb-2">
                                                            <Col md={12}>
                                                                <Form.Group>
                                                                    <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Buscar Palabra Clave al Abrir Catálogo (Opcional)</Form.Label>
                                                                    <Form.Control 
                                                                        size="sm"
                                                                        type="text"
                                                                        placeholder="Ej: arroz, leche, harina..."
                                                                        value={deeplinkBuilder.searchQuery}
                                                                        onChange={e => updateDeeplinkBuilder({ searchQuery: e.target.value })}
                                                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        {/* Alerta / Contador de productos en tiempo real con desglose de stock */}
                                                        {matchingProductsStats !== null && (
                                                            <div className={`d-flex align-items-start gap-2 p-2 mb-2 rounded ${
                                                                matchingProductsStats.total === 0
                                                                    ? 'bg-danger bg-opacity-10 text-danger border border-danger'
                                                                    : matchingProductsStats.withStock === 0
                                                                        ? 'bg-warning bg-opacity-10 text-warning border border-warning'
                                                                        : 'bg-success bg-opacity-10 text-success border border-success'
                                                            }`} style={{ fontSize: '11px' }}>
                                                                <i className={`mdi ${
                                                                    matchingProductsStats.total === 0
                                                                        ? 'mdi-alert-circle-outline'
                                                                        : matchingProductsStats.withStock === 0
                                                                            ? 'mdi-alert-outline'
                                                                            : 'mdi-check-circle-outline'
                                                                } mt-1`} style={{ fontSize: '16px' }}></i>
                                                                <div className="flex-grow-1">
                                                                    {matchingProductsStats.total === 0 ? (
                                                                        <>
                                                                            <strong>Atención: Este filtro no coincide con ningún producto en el catálogo actual.</strong>
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                                                Si guarda este filtro, el cliente verá el catálogo vacío al pulsar el banner. Considere cambiar la categoría o marca.
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                                                                                <strong>
                                                                                    {matchingProductsStats.total} producto{matchingProductsStats.total === 1 ? '' : 's'} en catálogo:
                                                                                </strong>
                                                                                <div className="d-flex gap-1">
                                                                                    <span className="badge bg-success" style={{ fontSize: '10px' }}>
                                                                                        {matchingProductsStats.withStock} disponible{matchingProductsStats.withStock === 1 ? '' : 's'} con stock
                                                                                    </span>
                                                                                    {matchingProductsStats.outOfStock > 0 && (
                                                                                        <span className="badge bg-secondary" style={{ fontSize: '10px' }}>
                                                                                            {matchingProductsStats.outOfStock} agotado{matchingProductsStats.outOfStock === 1 ? '' : 's'}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <div className="text-muted mt-1" style={{ fontSize: '10px' }}>
                                                                                {matchingProductsStats.withStock === 0 ? (
                                                                                    'Nota: Aunque existen productos registrados en este filtro, ninguno tiene stock disponible actualmente (la App móvil solo muestra productos con stock).'
                                                                                ) : (
                                                                                    `La App móvil mostrará ${matchingProductsStats.withStock} producto${matchingProductsStats.withStock === 1 ? '' : 's'} con stock para compra inmediata.`
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {deeplinkBuilder.mode === 'screen' && (
                                                    <Form.Group>
                                                        <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Seleccione la Pantalla Interna de la App</Form.Label>
                                                        <Form.Select 
                                                            value={deeplinkBuilder.screen}
                                                            onChange={e => updateDeeplinkBuilder({ screen: e.target.value })}
                                                            style={{ borderRadius: '8px', fontSize: '13px' }}
                                                        >
                                                            <option value="InvoicesPage">Mis Facturas y Cuentas por Cobrar (InvoicesPage)</option>
                                                            <option value="CartPage">Carrito de Compras (CartPage)</option>
                                                            <option value="FaqPage">Centro de Preguntas Frecuentes y Soporte (FaqPage)</option>
                                                            <option value="NotificationsPage">Bandeja de Notificaciones (NotificationsPage)</option>
                                                            <option value="ProfilePage">Mi Perfil / Datos de Cuenta (ProfilePage)</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                 )}

                                                {/* Vista previa de la ruta generada */}
                                                <div className="mt-2 p-2 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: isDark ? '#10172d' : '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                                                    <div>
                                                        <small className="text-muted d-block fw-bold" style={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                                            <i className="mdi mdi-link-variant me-1"></i> Ruta que se ejecutará en la App:
                                                        </small>
                                                        <code style={{ color: '#183eeb', fontWeight: 'bold', fontSize: '12px' }}>
                                                            {bannerForm.target_id || 'CatalogPage'}
                                                        </code>
                                                    </div>
                                                    <span className="badge" style={{ backgroundColor: '#183eeb', fontSize: '10px' }}>
                                                        100% Válida para Shell
                                                    </span>
                                                </div>
                                            </Card>
                                        </Col>
                                    )}
                                </Row>

                                {(bannerForm.action_type === 'catalog' || bannerForm.action_type === 'invoices') && (
                                    <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '12px', borderRadius: '8px' }}>
                                        <i className="mdi mdi-information-outline me-1"></i>
                                        {bannerForm.action_type === 'invoices' && 'Al pulsar el banner, la app abrirá directamente la pantalla de facturas y cuentas por pagar del cliente.'}
                                        {bannerForm.action_type === 'catalog' && 'Al pulsar el banner, la app abrirá el catálogo completo.'}
                                    </div>
                                )}

                                <hr className="my-3" style={{ opacity: 0.15 }} />

                                {/* SECCIÓN: DISEÑO Y CONTRASTE */}
                                <h6 className="fw-bold mb-2" style={{ color: '#183eeb' }}>
                                    <i className="mdi mdi-palette-outline me-1"></i> Diseño, Degradado y Contraste
                                </h6>
                                <div className="mb-2">
                                    <small className="text-muted fw-bold d-block mb-1">Paletas Predefinidas Rápidas:</small>
                                    <div className="d-flex flex-wrap gap-1">
                                        {BANNER_COLOR_PRESETS.map(preset => (
                                            <Button
                                                key={preset.id}
                                                type="button"
                                                size="sm"
                                                variant={isDark ? 'outline-light' : 'outline-secondary'}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '4px 9px',
                                                    borderRadius: '6px',
                                                    borderColor: isDark ? '#334155' : '#cbd5e1',
                                                    backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                                    color: isDark ? '#f8fafc' : '#334155'
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setBannerForm(prev => ({
                                                        ...prev,
                                                        bg_color_start: preset.start,
                                                        bg_color_end: preset.end,
                                                        text_color: preset.text,
                                                        btn_bg_color: preset.btnBg,
                                                        btn_text_color: preset.btnText
                                                    }));
                                                }}
                                            >
                                                <span 
                                                    style={{ 
                                                        display: 'inline-block', 
                                                        width: '10px', 
                                                        height: '10px', 
                                                        borderRadius: '50%', 
                                                        background: `linear-gradient(135deg, ${preset.start}, ${preset.end})`,
                                                        marginRight: '6px',
                                                        verticalAlign: 'middle'
                                                    }} 
                                                />
                                                {preset.name}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <Row className="mt-2">
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Fondo Gradiente (Inicio)</Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control 
                                                    type="color" 
                                                    value={bannerForm.bg_color_start}
                                                    onChange={e => setBannerForm({...bannerForm, bg_color_start: e.target.value})}
                                                    style={{ width: '42px', height: '36px', padding: '2px', borderRadius: '6px' }}
                                                />
                                                <Form.Control 
                                                    type="text" 
                                                    value={bannerForm.bg_color_start}
                                                    onChange={e => setBannerForm({...bannerForm, bg_color_start: e.target.value})}
                                                    style={{ fontSize: '12px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Fondo Gradiente (Fin)</Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control 
                                                    type="color" 
                                                    value={bannerForm.bg_color_end}
                                                    onChange={e => setBannerForm({...bannerForm, bg_color_end: e.target.value})}
                                                    style={{ width: '42px', height: '36px', padding: '2px', borderRadius: '6px' }}
                                                />
                                                <Form.Control 
                                                    type="text" 
                                                    value={bannerForm.bg_color_end}
                                                    onChange={e => setBannerForm({...bannerForm, bg_color_end: e.target.value})}
                                                    style={{ fontSize: '12px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-1">
                                    <Col md={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                Texto de la Tarjeta (Tag, Título y Subtítulo)
                                            </Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control 
                                                    type="color" 
                                                    value={bannerForm.text_color}
                                                    onChange={e => setBannerForm({...bannerForm, text_color: e.target.value})}
                                                    style={{ width: '42px', height: '36px', padding: '2px', borderRadius: '6px' }}
                                                />
                                                <Button 
                                                    size="sm" 
                                                    variant={bannerForm.text_color === '#FFFFFF' ? 'primary' : 'outline-secondary'}
                                                    onClick={() => setBannerForm({...bannerForm, text_color: '#FFFFFF'})}
                                                    style={{ fontSize: '11px', padding: '4px 10px' }}
                                                >
                                                    <i className="mdi mdi-format-color-text me-1"></i> Texto Blanco
                                                </Button>
                                                <Button 
                                                    size="sm" 
                                                    variant={bannerForm.text_color === '#0F172A' ? 'dark' : 'outline-secondary'}
                                                    onClick={() => setBannerForm({...bannerForm, text_color: '#0F172A'})}
                                                    style={{ fontSize: '11px', padding: '4px 10px' }}
                                                >
                                                    <i className="mdi mdi-format-color-text me-1"></i> Texto Oscuro
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-1">
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Color de Fondo del Botón</Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control 
                                                    type="color" 
                                                    value={bannerForm.btn_bg_color}
                                                    onChange={e => setBannerForm({...bannerForm, btn_bg_color: e.target.value})}
                                                    style={{ width: '42px', height: '36px', padding: '2px', borderRadius: '6px' }}
                                                />
                                                <Form.Control 
                                                    type="text" 
                                                    value={bannerForm.btn_bg_color}
                                                    onChange={e => setBannerForm({...bannerForm, btn_bg_color: e.target.value})}
                                                    style={{ fontSize: '12px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>Texto del Botón (Contraste)</Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control 
                                                    type="color" 
                                                    value={bannerForm.btn_text_color || '#FFFFFF'}
                                                    onChange={e => setBannerForm({...bannerForm, btn_text_color: e.target.value})}
                                                    style={{ width: '42px', height: '36px', padding: '2px', borderRadius: '6px' }}
                                                />
                                                <Form.Control 
                                                    type="text" 
                                                    value={bannerForm.btn_text_color || '#FFFFFF'}
                                                    onChange={e => setBannerForm({...bannerForm, btn_text_color: e.target.value})}
                                                    style={{ width: '85px', fontSize: '12px', borderRadius: '8px' }}
                                                />
                                                <Button 
                                                    size="sm" 
                                                    variant={bannerForm.btn_text_color === '#FFFFFF' ? 'primary' : 'outline-secondary'}
                                                    onClick={() => setBannerForm({...bannerForm, btn_text_color: '#FFFFFF'})}
                                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                                >
                                                    Blanco
                                                </Button>
                                                <Button 
                                                    size="sm" 
                                                    variant={bannerForm.btn_text_color === '#0F172A' ? 'dark' : 'outline-secondary'}
                                                    onClick={() => setBannerForm({...bannerForm, btn_text_color: '#0F172A'})}
                                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                                >
                                                    Oscuro
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" style={{ opacity: 0.15 }} />

                                <Form.Group className="mb-2">
                                    <Form.Check 
                                        type="switch"
                                        id="modal-switch-banner-active"
                                        label={<strong>{bannerForm.is_active ? 'Banner Activo (Visible en la app)' : 'Banner Inactivo (Pausado)'}</strong>}
                                        checked={bannerForm.is_active}
                                        onChange={e => setBannerForm({...bannerForm, is_active: e.target.checked})}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Columna Derecha: Previsualización de la Plantilla en la App */}
                            <Col md={5} className="d-flex flex-column align-items-center justify-content-center p-3" style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                <div className="text-center mb-3">
                                    <small className="fw-bold text-uppercase text-muted" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>
                                        <i className="mdi mdi-cellphone-android me-1"></i> Previsualización en Tiempo Real
                                    </small>
                                </div>

                                <div 
                                    className="banner-preview-element"
                                    style={{
                                        background: `linear-gradient(135deg, ${bannerForm.bg_color_start} 0%, ${bannerForm.bg_color_end} 100%)`,
                                        borderRadius: '16px',
                                        padding: '20px 22px',
                                        color: bannerForm.text_color,
                                        width: '100%',
                                        maxWidth: '320px',
                                        minHeight: '160px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <div>
                                        <div 
                                            className="banner-preview-text"
                                            style={{
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                letterSpacing: '1px',
                                                color: bannerForm.text_color,
                                                opacity: 0.85,
                                                textTransform: 'uppercase',
                                                marginBottom: '6px'
                                            }}
                                        >
                                            {bannerForm.tag || 'OFERTAS DE LA SEMANA'}
                                        </div>
                                        <div 
                                            className="banner-preview-text"
                                            style={{
                                                fontSize: '17px',
                                                fontWeight: 'bold',
                                                color: bannerForm.text_color,
                                                marginBottom: '6px',
                                                lineHeight: 1.25
                                            }}
                                        >
                                            {bannerForm.titulo || 'Título del Banner'}
                                        </div>
                                        {bannerForm.subtitulo && (
                                            <div 
                                                className="banner-preview-text"
                                                style={{
                                                    fontSize: '12px',
                                                    color: bannerForm.text_color,
                                                    opacity: 0.85,
                                                    marginBottom: '14px',
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {bannerForm.subtitulo}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <div 
                                            className="banner-preview-text"
                                            style={{
                                                display: 'inline-block',
                                                backgroundColor: bannerForm.btn_bg_color || '#183EEB',
                                                color: bannerForm.btn_text_color || '#FFFFFF',
                                                fontWeight: 'bold',
                                                fontSize: '11px',
                                                padding: '6px 16px',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                userSelect: 'none'
                                            }}
                                        >
                                            <span 
                                                className="banner-preview-text" 
                                                style={{ color: `${bannerForm.btn_text_color || '#FFFFFF'} !important` }}
                                            >
                                                {bannerForm.action_label || 'Ver ahora'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <small className="text-muted text-center mt-3" style={{ fontSize: '11px' }}>
                                    Gradiente y contraste aplicados en vivo tal como se verá en la app móvil.
                                </small>
                            </Col>
                        </Row>
                    </Modal.Body>
                                        <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowBannerModal(false)} style={{ borderRadius: '8px' }}>
                            {t('btn_cancel', 'Cancelar')}
                        </Button>
                        <Button type="submit" disabled={savingBanner} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold', padding: '8px 20px' }}>
                            {savingBanner ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL LABOR DE APOYO (MERCADERISTA) */}
            <Modal show={showMerchandisingTaskModal} onHide={() => setShowMerchandisingTaskModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#010028', fontWeight: 'bold' }}>
                        {merchandisingTaskForm.id ? t('btn_edit', 'Editar') : t('btn_add', 'Agregar')} - {t('tab_merchandising_task', 'Labor de Apoyo')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveMerchandisingTask}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_task_code', 'Código Interno (Mayúsculas)')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_task_code', 'Ej: REBANADO_BANDEJEO, ROTACION_FIFO')}
                                value={merchandisingTaskForm.code}
                                onChange={e => setMerchandisingTaskForm({...merchandisingTaskForm, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_task_name', 'Descripción de la Labor')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required 
                                placeholder={t('placeholder_task_name', 'Ej: Rebanado y Bandejeo en Charcutería')}
                                value={merchandisingTaskForm.name}
                                onChange={e => setMerchandisingTaskForm({...merchandisingTaskForm, name: e.target.value})}
                                style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none' }}>
                        <Button variant="light" onClick={() => setShowMerchandisingTaskModal(false)} style={{ borderRadius: '8px' }}>{t('btn_cancel', 'Cancelar')}</Button>
                        <Button type="submit" disabled={savingMerchandisingTask} style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
                            {savingMerchandisingTask ? t('loading', 'Guardando...') : t('btn_save', 'Guardar')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default MasterDataConfig;
